/**
 * CEO Dashboard Page
 * 
 * Executive dashboard for CEO only access.
 * Features:
 * - AI Daily Brief with structured sections
 * - Live Sales Snapshot
 * - Sales Team Overview
 * - Marketing Snapshot
 * - Competition Leaderboard Preview
 * - Weekly Trend Charts
 * - CEO-HOD Comments System
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import LinkedToSelector, { LinkedToDisplayChip } from '../components/LinkedToSelector';
import VoiceMessageRecorder from '../components/VoiceMessageRecorder';
import {
  Building2,
  Crown,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Target,
  AlertTriangle,
  FileText,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Send,
  Mic,
  MessageSquare,
  Clock,
  Calendar,
  Trophy,
  Zap,
  Eye,
  CheckCircle2,
  XCircle,
  Sparkles,
  BarChart3,
  Megaphone,
  Building,
  Activity,
  Mail,
  X,
  Plus,
  MapPin,
  LogIn,
  LogOut,
  Timer,
  LayoutDashboard,
  User,
} from 'lucide-react';

// Phase 1 CRM Enhancement Components
import AgentStatusDashboard from '../components/AgentStatusDashboard';
import AgentsOverviewPanel from '../components/AgentsOverviewPanel';
import TeamSLAMonitor from '../components/TeamSLAMonitor';
import ResponseTimeCard from '../components/ResponseTimeCard';
import NotificationBell from '../components/NotificationBell';

// Phase 2 CRM Enhancement Components
import LiveLocationMap from '../components/LiveLocationMap';
import TeamSiteVisitsPanel from '../components/TeamSiteVisitsPanel';
import SiteVisitReportsPanel from '../components/SiteVisitReportsPanel';

// Shared dashboard snapshot panels (Sales + Marketing) with target editing
import {
  SalesSnapshotPanel as SharedSalesSnapshotPanel,
  MarketingSnapshotPanel as SharedMarketingSnapshotPanel,
} from '../components/SnapshotPanels';
import { progressiveFetch } from '../utils/progressiveFetch';
import HelpInfo from '../components/HelpInfo';
import DirhamSign from '../components/DirhamSign';
import AlertsFlagsPanel from '../components/AlertsFlagsPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============ Alert Quick Actions (per-row Feedback / Warning) ============
// Renders two buttons under each alert. Clicking opens an inline composer
// with the recipient prefilled (the responsible HOD based on department)
// and posts to /api/ceo/comments with the appropriate priority. The HOD
// receives a bell notification + an entry in their CEO Comments thread.
const AlertActions = ({ alert, token, darkMode }) => {
  const [mode, setMode] = useState(null); // null | 'feedback' | 'warning'
  // 'both' fans the message out to both the agents AND their HOD so the
  // whole accountability chain sees it; 'hod' or 'agents' narrow the scope.
  const [audience, setAudience] = useState('both');
  const [text, setText] = useState('');
  // Track the last auto-prefilled body so we can refresh it when the
  // audience changes — without clobbering text the CEO has manually edited.
  const [autoText, setAutoText] = useState('');
  const [sending, setSending] = useState(false);
  const [agents, setAgents] = useState([]); // department's agents, fetched on first need

  const targetHod = alert.department === 'Marketing' ? 'hod_marketing'
                  : alert.department === 'Sales' ? 'hod_sales'
                  : 'all';
  // Title text mentions agents → default to agent broadcast so the people
  // actually called out by the alert receive the message in their inbox.
  const mentionsAgents = /\bagent/i.test(`${alert.title || ''} ${alert.message || ''}`);

  // Pull the department roster when the user picks the Agent audience the
  // first time. Filtered by department so a Sales alert doesn't ping the
  // marketing team and vice versa.
  const loadAgents = async () => {
    if (agents.length || !token) return;
    try {
      const res = await fetch(`${API_URL}/api/hod/messageable-agents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const all = data?.data?.agents || [];
      const dept = (alert.department || '').toLowerCase();
      const filtered = all.filter((a) => {
        const role = (a.role || '').toLowerCase();
        if (!dept) return true;
        if (dept === 'sales') return role.includes('sales') || role === 'cr_agent';
        if (dept === 'marketing') return role.includes('marketing');
        return true;
      });
      setAgents(filtered);
    } catch (e) {
      // non-fatal
    }
  };

  // Compose a body tailored to the audience and the action mode. Warnings
  // are escalatory and accountability-heavy; feedback is informational and
  // coaching-toned. Six distinct templates cover the matrix.
  const composeText = (m, aud) => {
    const verb = m === 'warning' ? 'WARNING' : 'Feedback';
    const head = `${verb}: ${alert.title} — ${alert.message}`;
    const dept = alert.department || '';
    const hod = alert.hod_name || 'HOD';

    if (m === 'warning') {
      if (aud === 'agents') {
        return `${head}\n\nThis is a formal warning. Your inactivity has been flagged at the executive level. Update your status, log today's field activity, and reply here with an explanation within the next hour. ${hod} (${dept}) is copied on this message, and continued silence will be escalated to your performance record.`;
      }
      if (aud === 'hod') {
        return `${head}\n\n${hod} — this is a management warning. The ${dept || 'team'} situation is unacceptable and you are directly accountable. I need a corrective action plan and progress update from you by end of day. Repeat occurrences will be reflected in your review.`;
      }
      // both
      return `${head}\n\n${dept || ''} agents — formal warning: update your status and log activity right now, then reply with what you were doing.\n${hod} (${dept}) — you are accountable for this team. Step in, drive a fix, and report back today. Both sides are on this message; I expect coordinated follow-up.`;
    }

    // feedback
    if (aud === 'agents') {
      return `${head}\n\nWanted to flag this directly with you. Please log your status and any field updates today, and reply here with a quick note on what's happening. ${hod} (${dept}) has been kept in the loop and is here to help if you need it.`;
    }
    if (aud === 'hod') {
      return `${head}\n\n${hod} — flagging this for your visibility. Please look into it with your ${dept || 'team'} and reply with your read of the situation and what you plan to do about it.`;
    }
    // both
    return `${head}\n\n${dept || ''} agents: please update your status and log today's activity — drop a quick note here if anything's blocking you.\n${hod} (${dept}): keep a close eye on this and align with your team; let me know how I can help.`;
  };

  const start = (m) => {
    setMode(m);
    // Default: when the alert calls out agents, send to BOTH the agents
    // and their HOD so both sides of the chain see it. Otherwise HOD only.
    const initialAud = mentionsAgents ? 'both' : 'hod';
    setAudience(initialAud);
    const t = composeText(m, initialAud);
    setText(t);
    setAutoText(t);
    if (mentionsAgents) loadAgents();
  };

  // When audience changes after the composer is open, refresh the body —
  // but only if the user hasn't typed over the previous auto-text.
  const switchAudience = (next) => {
    setAudience(next);
    if (next !== 'hod') loadAgents();
    if (text === autoText) {
      const t = composeText(mode, next);
      setText(t);
      setAutoText(t);
    }
  };

  const cancel = () => { setMode(null); setText(''); setAutoText(''); };

  const sendToHod = () => fetch(`${API_URL}/api/ceo/comments`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      comment_text: text,
      target_hod: targetHod,
      priority: mode === 'warning' ? 'urgent' : 'normal',
      linked_to: { recordType: 'alert', recordId: alert.id, recordLabel: alert.title },
    }),
  }).then((r) => r.json());

  const sendToAgents = async () => {
    if (agents.length === 0) await loadAgents();
    const list = agents.length ? agents : [];
    if (!list.length) return { ok: 0, total: 0 };
    const results = await Promise.allSettled(list.map((a) => {
      const id = a.id || a.agent_id;
      return fetch(`${API_URL}/api/hod/agents/${id}/direct-message`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ feedback_text: text, agent_id: id }),
      }).then((r) => r.json());
    }));
    return {
      ok: results.filter((r) => r.status === 'fulfilled' && r.value?.success).length,
      total: list.length,
    };
  };

  const send = async () => {
    if (!text.trim()) { toast.error('Add a message before sending'); return; }
    setSending(true);
    try {
      const verb = mode === 'warning' ? 'Warning' : 'Feedback';
      if (audience === 'hod') {
        const data = await sendToHod();
        if (!data?.success) { toast.error(data?.error || data?.detail || 'Failed to send'); return; }
        toast.success(`${verb} sent to HOD`);
      } else if (audience === 'agents') {
        const { ok, total } = await sendToAgents();
        if (!total) { toast.error('No agents loaded for this department'); return; }
        toast.success(`${verb} sent to ${ok} of ${total} agents`);
      } else {
        // 'both' — send to the HOD AND every agent in their team.
        const [hodResult, agentResult] = await Promise.all([sendToHod(), sendToAgents()]);
        const hodOk = !!hodResult?.success;
        const { ok: aOk, total: aTotal } = agentResult;
        toast.success(`${verb} sent to ${aOk} of ${aTotal} agents${hodOk ? ' + HOD' : ' (HOD failed)'}`);
      }
      cancel();
    } catch (e) {
      toast.error(`Send failed: ${e.message || e}`);
    } finally {
      setSending(false);
    }
  };

  const hodLabel = targetHod === 'all' ? 'all HODs' : targetHod.replace('_', ' ').toUpperCase();
  const agentsLabel = `${agents.length || '…'} ${alert.department || ''} agent${agents.length === 1 ? '' : 's'}`;
  const audienceLabel = audience === 'hod'
    ? hodLabel
    : audience === 'agents'
      ? agentsLabel
      : `${agentsLabel} + ${hodLabel}`;

  if (!mode) {
    return (
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-current/10 flex-wrap">
        <Button size="sm" variant="outline" onClick={() => start('feedback')}>
          <MessageSquare size={13} className="mr-1" /> Send Feedback
        </Button>
        <Button size="sm" className="bg-red-600 hover:bg-red-500 text-white" onClick={() => start('warning')}>
          <AlertTriangle size={13} className="mr-1" /> Send Warning
        </Button>
        <span className={`text-xs ml-auto ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
          → defaults to {mentionsAgents ? `${alert.department || ''} agents + HOD` : `HOD ${alert.department || ''}`}
        </span>
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-current/10 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <p className={`text-xs font-semibold ${mode === 'warning' ? 'text-red-600' : 'text-emerald-600'}`}>
          {mode === 'warning' ? 'Sending WARNING to' : 'Sending feedback to'} {audienceLabel}
        </p>
        <div className="ml-auto flex items-center gap-1 flex-wrap">
          <span className="text-xs text-slate-500">Send to:</span>
          {[
            { v: 'both', label: 'Agents + HOD' },
            { v: 'agents', label: 'Agents only' },
            { v: 'hod', label: 'HOD only' },
          ].map((opt) => (
            <Button
              key={opt.v}
              size="sm"
              variant={audience === opt.v ? 'default' : 'outline'}
              className={audience === opt.v
                ? (mode === 'warning' ? 'bg-red-600 hover:bg-red-500' : 'bg-emerald-600 hover:bg-emerald-500') + ' text-white h-7 text-xs px-2'
                : 'h-7 text-xs px-2'}
              onClick={() => switchAudience(opt.v)}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={3}
        placeholder="Write a clear, specific message…"
        className={darkMode ? 'bg-slate-800 border-slate-600 text-white' : ''}
      />
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={cancel} disabled={sending}>Cancel</Button>
        <Button
          size="sm"
          onClick={send}
          disabled={sending}
          className={mode === 'warning' ? 'bg-red-600 hover:bg-red-500 text-white' : 'bg-emerald-600 hover:bg-emerald-500 text-white'}
        >
          {sending ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />}
          {mode === 'warning' ? 'Send Warning' : 'Send Feedback'}
        </Button>
      </div>
    </div>
  );
};

// Opens a read-only dialog showing the submitted site-visit report. Shown
// next to the "Report Submitted" badge on each movement row so the CEO can
// inspect what the HOD actually filed.
const MovementOpenReportButton = ({ movement, darkMode }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="outline" className="h-6 text-xs px-2" onClick={() => setOpen(true)}>
        <Eye size={11} className="mr-1" /> Open Report
      </Button>
      {open && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
            backgroundColor: 'rgba(15,23,42,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className={`w-full max-w-lg mx-4 rounded-2xl shadow-2xl border p-6 ${darkMode ? 'bg-[#1a1a2e] border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  Site Visit Report
                </h2>
                <p className="text-xs text-slate-500">
                  {movement.hod_name} · {movement.location}
                  {movement.duration_seconds != null && <> · {Math.floor(movement.duration_seconds / 60)}m on site</>}
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3 text-sm">
              {movement.report_outcome && (
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500">Outcome</p>
                  <p>{movement.report_outcome}</p>
                </div>
              )}
              {movement.report_notes && (
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500">Agent notes</p>
                  <p className="whitespace-pre-wrap">{movement.report_notes}</p>
                </div>
              )}
              {movement.report_next_steps && (
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500">Next steps</p>
                  <p className="whitespace-pre-wrap">{movement.report_next_steps}</p>
                </div>
              )}
              {movement.ai_narrative && (
                <div>
                  <p className="text-xs font-semibold uppercase text-emerald-700">AI narrative</p>
                  <div className="whitespace-pre-wrap rounded-md bg-emerald-50 border border-emerald-200 p-3 text-slate-800">
                    {movement.ai_narrative}
                  </div>
                </div>
              )}
              {!movement.report_outcome && !movement.report_notes && !movement.ai_narrative && (
                <p className="text-sm text-slate-500">No report content available.</p>
              )}
            </div>
            <div className="flex justify-end mt-4">
              <Button size="sm" variant="outline" onClick={() => setOpen(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Send-feedback dialog used when the agent skipped the post-visit report.
// CEO can choose to send to the HOD only, or to both the agent (mirrored
// hod via department) AND the HOD.
const MovementFeedbackButton = ({ movement, token, darkMode }) => {
  const [open, setOpen] = useState(false);
  const [audience, setAudience] = useState('both'); // 'both' | 'hod'
  const [text, setText] = useState('');
  const [autoText, setAutoText] = useState('');
  const [sending, setSending] = useState(false);

  const targetHod = movement.department === 'Marketing' ? 'hod_marketing'
                  : movement.department === 'Sales' ? 'hod_sales'
                  : 'all';

  // Different tone depending on who's receiving the message. 'both' sends
  // the HOD a directive and broadcasts to every team agent so the whole
  // department sees the lapse.
  const composeText = (aud) => {
    const who = movement.hod_name || 'the HOD';
    const place = movement.location || 'the site';
    const dept = movement.department || '';
    if (aud === 'hod') {
      return `${who} skipped the post-visit report for ${place} today.\n\nPlease follow up with them and make sure the report is filed before end of day — we need the outcome and next steps on record. Reply here once it's done so I have the trail.`;
    }
    // both — HOD gets a directive in their CEO Comments; team agents get a
    // copy in their Feedback from Management so everyone has visibility.
    return `Team note — ${who}'s post-visit report for ${place} was skipped today.\n\n${who} (${dept} HOD): please own this and get the report filed today.\n${dept || 'Team'} agents: this lands in your inbox so you have visibility — please make sure your own visit reports are submitted on time so we keep the bar high.`;
  };

  const start = () => {
    setOpen(true);
    const initial = 'both';
    setAudience(initial);
    const t = composeText(initial);
    setText(t);
    setAutoText(t);
  };

  // Switching audience refreshes the body unless the CEO has manually edited it.
  const switchAudience = (next) => {
    setAudience(next);
    if (text === autoText) {
      const t = composeText(next);
      setText(t);
      setAutoText(t);
    }
  };

  const send = async () => {
    if (!text.trim()) { toast.error('Add a message before sending'); return; }
    setSending(true);
    try {
      // HOD always gets it via the CEO comments thread.
      const hodTask = fetch(`${API_URL}/api/ceo/comments`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          comment_text: text,
          target_hod: targetHod,
          priority: 'normal',
          linked_to: { recordType: 'visit', recordId: movement.visit_id || movement.id, recordLabel: movement.location },
        }),
      }).then((r) => r.json());

      let agentCount = 0;
      let agentTask = null;
      if (audience === 'both') {
        // Pull the team's agents in the same department and fan the
        // message out to each one's Feedback from Management card. This
        // gives the whole team visibility that the HOD missed a report.
        try {
          const agentsRes = await fetch(`${API_URL}/api/hod/messageable-agents`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const agentsData = await agentsRes.json();
          const all = agentsData?.data?.agents || [];
          const dept = (movement.department || '').toLowerCase();
          const teamAgents = all.filter((a) => {
            const role = (a.role || '').toLowerCase();
            if (!dept) return true;
            if (dept === 'sales') return role.includes('sales') || role === 'cr_agent';
            if (dept === 'marketing') return role.includes('marketing');
            return true;
          });
          agentCount = teamAgents.length;
          agentTask = Promise.allSettled(
            teamAgents.map((a) => {
              const id = a.id || a.agent_id;
              return fetch(`${API_URL}/api/hod/agents/${id}/direct-message`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ feedback_text: text, agent_id: id }),
              }).then((r) => r.json());
            })
          );
        } catch (e) {
          // non-fatal — HOD still gets the message
        }
      }

      const [hodResult, agentResults] = await Promise.all([hodTask, agentTask || Promise.resolve(null)]);
      const hodOk = !!hodResult?.success;
      const aOk = Array.isArray(agentResults)
        ? agentResults.filter((r) => r.status === 'fulfilled' && r.value?.success).length
        : 0;

      if (audience === 'both') {
        toast.success(`Feedback sent to HOD${hodOk ? '' : ' (failed)'} + ${aOk}/${agentCount} ${movement.department || ''} agents`);
      } else {
        toast[hodOk ? 'success' : 'error'](hodOk ? 'Feedback sent to HOD' : 'Failed to send to HOD');
      }
      setOpen(false);
    } catch (e) {
      toast.error(`Send failed: ${e.message || e}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Button size="sm" className="h-6 text-xs px-2 bg-amber-600 hover:bg-amber-500 text-white" onClick={start}>
        <Send size={11} className="mr-1" /> Send Feedback
      </Button>
      {open && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
            backgroundColor: 'rgba(15,23,42,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={(e) => { if (e.target === e.currentTarget && !sending) setOpen(false); }}
        >
          <div className={`w-full max-w-lg mx-4 rounded-2xl shadow-2xl border p-6 ${darkMode ? 'bg-[#1a1a2e] border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  Send feedback for skipped report
                </h2>
                <p className="text-xs text-slate-500">
                  {movement.hod_name} · {movement.location}
                </p>
              </div>
              <button onClick={() => !sending && setOpen(false)} className="text-slate-400 hover:text-slate-600">
                <X size={18} />
              </button>
            </div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span className="text-xs text-slate-500">Send to:</span>
              {[
                { v: 'both', label: 'HOD + Agent' },
                { v: 'hod', label: 'HOD only' },
              ].map((opt) => (
                <Button
                  key={opt.v}
                  size="sm"
                  variant={audience === opt.v ? 'default' : 'outline'}
                  className={audience === opt.v ? 'bg-emerald-600 hover:bg-emerald-500 text-white h-7 text-xs px-2' : 'h-7 text-xs px-2'}
                  onClick={() => switchAudience(opt.v)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              placeholder="Write a short message…"
              className={darkMode ? 'bg-slate-800 border-slate-600 text-white' : ''}
              disabled={sending}
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button size="sm" variant="ghost" onClick={() => setOpen(false)} disabled={sending}>Cancel</Button>
              <Button size="sm" onClick={send} disabled={sending} className="bg-emerald-600 hover:bg-emerald-500 text-white">
                {sending ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />}
                Send Feedback
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ============ Comments from CEO — recipient-style card feed ============
// Mirrors the layout of the HOD's "Comments from CEO" panel so the
// CEO Dashboard's CEO Comments tab matches the HOD view visually.
// The CEO is the *sender*, so the Reply button posts a follow-up to
// the same thread (same comment id) — useful for clarifying without
// starting a new thread.
const CEOSentCommentsFeed = ({ comments = [], token, onSendComment, darkMode }) => {
  const [replyOpen, setReplyOpen] = useState(null);   // comment.id
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const unread = comments.filter((c) => !c.is_read_by_hod).length;
  const urgent = comments.filter((c) => c.priority === 'urgent').length;

  // Format like "13 May 2026, 11:31 AM" — match the HOD card stamp.
  const fmt = (raw) => {
    if (!raw) return '';
    const s = String(raw);
    const d = new Date(/Z$|[+-]\d{2}:?\d{2}$/.test(s) ? s : `${s}Z`);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString('en-AE', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  };

  // Translate the comment's target_hod / agent fields to a coloured
  // chip matching the HOD-side palette.
  const targetChip = (c) => {
    if (c.target_hod === 'all') {
      return (
        <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium border border-amber-300 bg-amber-50 text-amber-700">
          @ All HODs
        </span>
      );
    }
    if (c.target_hod === 'hod_sales') {
      return (
        <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium border border-sky-300 bg-sky-50 text-sky-700">
          @ HOD Sales – HOD Sales
        </span>
      );
    }
    if (c.target_hod === 'hod_marketing') {
      return (
        <span className="inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium border border-violet-300 bg-violet-50 text-violet-700">
          @ HOD Marketing – HOD Marketing
        </span>
      );
    }
    return null;
  };

  const handleReply = async (commentId) => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      // Post a follow-up by re-targeting the original recipient.
      // The CEOCommentsSection's onSendComment handles the POST.
      if (onSendComment) {
        await onSendComment({
          text: replyText.trim(),
          target_hod: 'all', // sensible default for follow-up
          priority: 'normal',
          parent_id: commentId,
        });
      }
      setReplyText('');
      setReplyOpen(null);
    } catch (_) {
      // toast already handled in the parent
    } finally {
      setSending(false);
    }
  };

  if (comments.length === 0) {
    return (
      <div className={`text-center py-10 rounded-xl border border-dashed ${
        darkMode ? 'border-slate-700 text-slate-400' : 'border-slate-200 text-slate-500'
      }`}>
        <MessageSquare size={28} className="mx-auto mb-2 opacity-40" />
        <p className="text-sm font-medium">No comments yet</p>
        <p className="text-xs mt-1">Use the composer below to start a thread.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header — crown icon + title + unread badge + subtitle */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <Crown size={18} className="text-violet-500" />
          <h4 className={`font-medium text-base ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            Comments to HODs
          </h4>
          {unread > 0 && (
            <Badge variant="destructive" className="text-xs font-semibold px-2.5 py-0.5">
              {unread} unread
            </Badge>
          )}
        </div>
        <p className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          Private communication channel with HODs
        </p>
        {(unread > 0 || urgent > 0) && (
          <p className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {unread} unread{urgent > 0 ? ` (${urgent} urgent)` : ''}
          </p>
        )}
      </div>

      {/* Card list */}
      <div className="space-y-3">
        {comments.map((c) => {
          const isUnread = !c.is_read_by_hod;
          // The agent rows on the HOD side include an "Agent: <name>"
          // chip when the comment was tagged to a specific agent.
          const agentChip = c.recipient_agent_name ? (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200">
              <Users size={11} /> Agent {c.recipient_agent_name}
            </span>
          ) : null;

          return (
            <div
              key={c.id}
              className={`rounded-xl border p-4 ${
                isUnread
                  ? darkMode ? 'border-violet-700/40 bg-violet-900/10' : 'border-violet-200 bg-violet-50/40'
                  : darkMode ? 'border-slate-700 bg-slate-800/40' : 'border-slate-200 bg-white'
              }`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                {/* Left — sender + tag chips */}
                <div className="flex items-center gap-2 flex-wrap min-w-0 flex-1">
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-violet-600">
                    <Crown size={13} /> From CEO
                  </span>
                  {agentChip}
                  <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    Tagged:
                  </span>
                  {targetChip(c)}
                </div>
                {/* Right — priority + new badge */}
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-xs px-2.5 py-0.5 rounded-full border ${
                    c.priority === 'urgent'
                      ? 'border-red-200 bg-red-50 text-red-700'
                      : 'border-slate-200 bg-slate-50 text-slate-600'
                  }`}>
                    {c.priority || 'normal'}
                  </span>
                  {isUnread && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-600 text-white font-semibold">
                      New
                    </span>
                  )}
                </div>
              </div>

              {/* Body */}
              <p className={`mt-3 text-sm whitespace-pre-wrap ${darkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                {c.comment_text || c.text || '(no body)'}
              </p>

              {/* Footer — timestamp + Reply trigger */}
              <div className="mt-3 flex items-center gap-3 flex-wrap">
                <span className={`text-[11px] flex items-center gap-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  <Clock size={11} /> {fmt(c.created_at)}
                </span>
              </div>

              {/* Reply thread — was missing on the CEO side, so a CEO
                  who followed up on their own thread (or received an
                  HOD reply) saw nothing. Mirrors the HOD Dashboard
                  render so both sides see the same conversation. */}
              {Array.isArray(c.conversation) && c.conversation.length > 0 && (
                <div className={`mt-4 space-y-2 pl-3 ${darkMode ? 'border-l-2 border-slate-600' : 'border-l-2 border-slate-300'}`}>
                  {c.conversation.map((msg, idx) => {
                    const isCeoReply = msg.sender_role === 'ceo' || msg.sender_role === 'super_admin';
                    const label = isCeoReply
                      ? 'CEO Reply'
                      : msg.sender_role === 'hod_sales'
                          ? 'HOD Sales Reply'
                          : msg.sender_role === 'hod_marketing'
                              ? 'HOD Marketing Reply'
                              : 'HOD Reply';
                    const rawTs = msg.created_at || msg.sent_at || msg.timestamp || msg.replied_at;
                    const parsed = rawTs ? new Date(rawTs) : null;
                    const tsStr = parsed && !Number.isNaN(parsed.getTime()) ? fmt(rawTs) : null;
                    return (
                      <div
                        key={msg.id || idx}
                        className={`p-3 rounded-lg text-sm ${
                          isCeoReply
                            ? darkMode
                                ? 'bg-violet-900/30 border border-violet-700/50 text-violet-100'
                                : 'bg-violet-50 border border-violet-200 text-slate-800'
                            : darkMode
                                ? 'bg-slate-700/40 border border-slate-600 text-slate-100'
                                : 'bg-slate-50 border border-slate-200 text-slate-800'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`text-xs font-semibold ${
                            isCeoReply
                              ? darkMode ? 'text-violet-300' : 'text-violet-700'
                              : darkMode ? 'text-slate-300' : 'text-slate-700'
                          }`}>
                            {label}
                          </span>
                          {(msg.sender_name || msg.author_name) && (
                            <span className={`text-[11px] ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              · from{' '}
                              <span className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                                {msg.sender_name || msg.author_name}
                              </span>
                            </span>
                          )}
                        </div>
                        <p className="whitespace-pre-wrap">{msg.text || msg.message || ''}</p>
                        {tsStr && (
                          <div className={`mt-1 text-[11px] flex items-center gap-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            <Clock size={10} />
                            {tsStr}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="mt-2">
                {replyOpen === c.id ? (
                  <div className="space-y-2">
                    <Textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Add a follow-up to this thread…"
                      rows={2}
                      className="resize-none"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleReply(c.id)}
                        disabled={sending || !replyText.trim()}
                        className="bg-violet-600 hover:bg-violet-700 text-white"
                      >
                        <Send size={12} className="mr-1" />
                        {sending ? 'Sending…' : 'Send reply'}
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => { setReplyOpen(null); setReplyText(''); }}
                        disabled={sending}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    size="sm" variant="outline"
                    onClick={() => setReplyOpen(c.id)}
                    className="text-violet-700 border-violet-300 hover:bg-violet-50"
                  >
                    <Send size={12} className="mr-1 rotate-180" />
                    Reply
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============ CEO Lead Dashboard Panel ============
// Rendered when the CEO picks "Lead Dashboard" from the scope pill.
// The CEO doesn't need the operational bells-and-whistles a BDM sees
// on the standalone /leads-dashboard page — they need the exec cut:
// how many leads across each flow, who's contributing, and where the
// pipeline actually sits. Reuses lightweight summary endpoints so the
// panel paints fast even with a slow BDM/Brokerage backend.
const CEOLeadDashboardPanel = ({ token, darkMode }) => {
  const navigate = useNavigate();
  const [counts, setCounts] = React.useState({
    directLeads:      null,
    indirectLeads:    null,
    brokerages:       null,
    brokerageLeads:   null,
    lifecycleStages:  null,
    activeBdms:       null,
    monthQualified:   null,
  });
  const [topBdms, setTopBdms] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const settle = (p) => p.then((r) => r).catch(() => null);

  const fetchAll = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const API = process.env.REACT_APP_BACKEND_URL;
    const [
      direct, indirect, brokerages, brokerageLeads, lifecycle,
      bdms, thisMonthLeads,
    ] = await Promise.all([
      settle(axios.get(`${API}/api/contacts`, { params: { token, source: 'direct',   limit: 1 } })),
      settle(axios.get(`${API}/api/contacts`, { params: { token, source: 'indirect', limit: 1 } })),
      settle(axios.get(`${API}/api/brokerage-hub/brokerages`,   { params: { token } })),
      settle(axios.get(`${API}/api/brokerage-hub/leads-summary`,{ params: { token } })),
      settle(axios.get(`${API}/api/lifecycle-stages`,           { params: { token } })),
      // BDM roster + recent contribution — falls back gracefully if
      // either endpoint isn't available in this org's build.
      settle(axios.get(`${API}/api/accounts/users`,             { params: { token } })),
      settle(axios.get(`${API}/api/contacts`, {
        params: { token, ownership_filter: 'all', status: 'qualified', limit: 1 },
      })),
    ]);

    const brokerageList = Array.isArray(brokerages?.data?.data?.brokerages)
      ? brokerages.data.data.brokerages
      : Array.isArray(brokerages?.data?.brokerages) ? brokerages.data.brokerages : [];

    const userList = Array.isArray(bdms?.data) ? bdms.data
                   : Array.isArray(bdms?.data?.users) ? bdms.data.users
                   : Array.isArray(bdms?.data?.data) ? bdms.data.data : [];
    const bdmUsers = userList.filter((u) => {
      const role = String(u.role || '').toLowerCase();
      return role === 'bdm' && u.is_disabled !== true && u.is_active !== false;
    });

    // Top BDMs by brokerages they own (proxy for productivity when a
    // dedicated leaderboard endpoint isn't wired up yet).
    const bdmScores = bdmUsers.map((u) => {
      const owned = brokerageList.filter((b) => (b.bdm_id || b.assigned_bdm_id) === u.id).length;
      return { id: u.id, name: u.full_name || u.email || 'BDM', owned };
    }).sort((a, b) => b.owned - a.owned).slice(0, 5);

    setCounts({
      directLeads:     direct?.data?.total    ?? direct?.data?.count    ?? null,
      indirectLeads:   indirect?.data?.total  ?? indirect?.data?.count  ?? null,
      brokerages:      brokerageList.length || (brokerages?.data?.data?.count ?? null),
      brokerageLeads:  brokerageLeads?.data?.data?.total_leads ?? brokerageLeads?.data?.total ?? null,
      lifecycleStages: Array.isArray(lifecycle?.data?.stages)
        ? lifecycle.data.stages.length
        : (Array.isArray(lifecycle?.data) ? lifecycle.data.length : null),
      activeBdms:      bdmUsers.length,
      monthQualified:  thisMonthLeads?.data?.total ?? thisMonthLeads?.data?.count ?? null,
    });
    setTopBdms(bdmScores);
    setLoading(false);
  }, [token]);

  React.useEffect(() => { fetchAll(); }, [fetchAll]);

  const fmt = (n) => {
    if (n === null || n === undefined) return '—';
    const v = Number(n);
    if (!Number.isFinite(v)) return '—';
    return new Intl.NumberFormat('en-US').format(v);
  };

  const cards = [
    {
      key: 'direct', title: 'Direct Sales',
      description: 'Leads generated by your own sales agents.',
      icon: User, tone: 'bg-blue-100 text-blue-700', accent: 'border-blue-200',
      route: '/leads-dashboard/direct-sales',
      primary: { label: 'Total leads', value: fmt(counts.directLeads) },
    },
    {
      key: 'indirect', title: 'Indirect Sales',
      description: 'Leads referred by external partners (BDMs / indirect_sales).',
      icon: Users, tone: 'bg-indigo-100 text-indigo-700', accent: 'border-indigo-200',
      route: '/leads-dashboard/indirect-sales',
      primary: { label: 'Total leads', value: fmt(counts.indirectLeads) },
    },
    {
      key: 'brokerage', title: 'Brokerage Hub',
      description: 'External brokerage partner firms and every lead they source.',
      icon: Building, tone: 'bg-emerald-100 text-emerald-700', accent: 'border-emerald-200',
      route: '/leads-dashboard/brokerage-hub',
      primary: { label: 'Brokerages',   value: fmt(counts.brokerages) },
      secondary: { label: 'Broker leads', value: fmt(counts.brokerageLeads) },
    },
    {
      key: 'lifecycle', title: 'Lead Lifecycle',
      description: 'End-to-end journey of every lead across stages.',
      icon: Activity, tone: 'bg-violet-100 text-violet-700', accent: 'border-violet-200',
      route: '/lead-lifecycle',
      primary: { label: 'Stages configured', value: fmt(counts.lifecycleStages) },
    },
  ];

  return (
    <div className={`rounded-2xl border ${darkMode ? 'bg-[#1a1a2e] border-slate-700' : 'bg-white border-slate-200'} p-6 space-y-6`} data-testid="ceo-lead-dashboard">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
            <FileText size={22} />
          </div>
          <div>
            <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              Lead Dashboard
            </h2>
            <p className={`text-sm mt-0.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              CEO cut of every lead flow — Direct Sales, Indirect Sales, Brokerage Hub, Lead Lifecycle.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchAll}
          disabled={loading}
        >
          <RefreshCw size={14} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Top strip — executive counts */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Active BDMs',         value: fmt(counts.activeBdms) },
          { label: 'Partner brokerages',  value: fmt(counts.brokerages) },
          { label: 'Broker-sourced leads', value: fmt(counts.brokerageLeads) },
          { label: 'Qualified (all-time)', value: fmt(counts.monthQualified) },
        ].map((k) => (
          <div key={k.label} className={`rounded-lg border ${darkMode ? 'border-slate-700 bg-slate-800/40' : 'border-slate-200 bg-slate-50/60'} p-4`}>
            <div className={`text-[11px] uppercase tracking-wide ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{k.label}</div>
            <div className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              {loading && k.value === '—' ? '…' : k.value}
            </div>
          </div>
        ))}
      </div>

      {/* 4 flow cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cards.map((c) => (
          <div key={c.key} className={`rounded-xl border ${darkMode ? 'border-slate-700 bg-slate-800/30' : `bg-white ${c.accent} shadow-sm`} p-5 flex flex-col gap-3`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.tone}`}>
                <c.icon size={20} />
              </div>
              <div className="min-w-0">
                <div className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{c.title}</div>
                <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{c.description}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className={`rounded-md ${darkMode ? 'bg-slate-900/50' : 'bg-slate-50/70'} px-3 py-2`}>
                <div className={`text-[10px] uppercase tracking-wide ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{c.primary.label}</div>
                <div className={`text-lg font-bold mt-0.5 ${darkMode ? 'text-white' : 'text-slate-900'}`}>{c.primary.value}</div>
              </div>
              {c.secondary && (
                <div className={`rounded-md ${darkMode ? 'bg-slate-900/50' : 'bg-slate-50/70'} px-3 py-2`}>
                  <div className={`text-[10px] uppercase tracking-wide ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{c.secondary.label}</div>
                  <div className={`text-lg font-bold mt-0.5 ${darkMode ? 'text-white' : 'text-slate-900'}`}>{c.secondary.value}</div>
                </div>
              )}
            </div>
            <Button
              size="sm"
              variant="outline"
              className="self-start"
              onClick={() => navigate(c.route)}
            >
              Open {c.title}
              <ChevronRight size={14} className="ml-1" />
            </Button>
          </div>
        ))}
      </div>

      {/* Top BDMs by brokerage count. Falls back to a gentle empty
          state when the org has no BDM roster or the endpoint failed. */}
      <div className={`rounded-xl border ${darkMode ? 'border-slate-700 bg-slate-800/30' : 'border-slate-200 bg-white'} p-5`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Top BDMs by brokerages owned</div>
            <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Proxy for BDM productivity — who's bringing partners in.</div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/leads-dashboard/brokerage-hub')}>
            View Brokerage Hub <ChevronRight size={14} className="ml-1" />
          </Button>
        </div>
        {topBdms.length === 0 ? (
          <p className={`text-sm ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
            {loading ? 'Loading…' : 'No BDMs on record yet.'}
          </p>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-700">
            {topBdms.map((b, i) => (
              <li key={b.id} className="py-2 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className={`inline-flex w-6 h-6 items-center justify-center rounded-md text-xs font-semibold ${darkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-100 text-slate-600'}`}>{i + 1}</span>
                  <span className={darkMode ? 'text-slate-100' : 'text-slate-800'}>{b.name}</span>
                </div>
                <span className={`text-sm font-medium ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>{b.owned} brokerage{b.owned === 1 ? '' : 's'}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

// ============ HOD Dashboard View (Embedded in CEO Dashboard) ============
const HODDashboardView = ({
  token, darkMode, onDepartmentChange, selectedDepartment,
  agentRoster = [],
  // Comment-thread plumbing — when present, the embedded view's
  // "CEO Comments" tab uses these to render the same CEOCommentsSection
  // the page renders standalone below.
  comments = [],
  onSendComment, onMarkRead, onVoiceSent,
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  // Lazy-but-persistent tab mounting. We only render a tab's heavy body the
  // first time the user visits it; after that the body stays mounted (Radix's
  // forceMount keeps it in the DOM, hidden via the `hidden` attribute) so
  // re-visiting is an instant CSS show/hide with zero reconciliation cost.
  // Previously every parent re-render reconciled ALL six tab subtrees, which
  // is what made tab switches feel laggy on large lists.
  const [mountedTabs, setMountedTabs] = useState(() => new Set(['overview']));
  useEffect(() => {
    setMountedTabs((prev) => {
      if (prev.has(activeTab)) return prev;
      const next = new Set(prev);
      next.add(activeTab);
      return next;
    });
  }, [activeTab]);
  const [hodData, setHodData] = useState(null);
  const [hodList, setHodList] = useState([]);
  const [hodReports, setHodReports] = useState([]);
  const [hodTasks, setHodTasks] = useState([]);
  const [hodAlerts, setHodAlerts] = useState([]);
  // Per-HOD "Alerts & Flags" — the same feed the HOD dashboard uses,
  // filtered to HOD-subject alerts. The CEO sees flags raised against
  // HODs here; agent-subject flags stay on each HOD's own dashboard.
  const [hodFlagAlerts, setHodFlagAlerts] = useState([]);
  const [hodMovements, setHodMovements] = useState([]);

  // Small helper that returns the human label for the active
  // department filter — used as a prefix/suffix on tab headings so a
  // CEO scanning the page sees "Sales Heads of Department" instead
  // of a generic "Heads of Department".
  const deptLabel = selectedDepartment === 'sales'
    ? 'Sales'
    : selectedDepartment === 'marketing'
      ? 'Marketing'
      : '';

  // User lookup combining HODs (from /ceo/hods, carrying their
  // department) AND individual-contributor agents (from the parent's
  // agentRoster, carrying their role). Tasks/alerts/movements that
  // only carry `assigned_to` (a user_id) can resolve through this map
  // to determine their department.
  const hodById = useMemo(() => {
    const m = new Map();
    (hodList || []).forEach((h) => {
      const dept = h.department || '';
      if (h?.hod_id)  m.set(h.hod_id,  { department: dept, role: h.role || 'hod' });
      if (h?.id)      m.set(h.id,      { department: dept, role: h.role || 'hod' });
      if (h?.user_id) m.set(h.user_id, { department: dept, role: h.role || 'hod' });
    });
    // Agents carry `role` (sales_agent / marketing_agent / cr_agent /
    // telesales / sales_rep) — derive a synthetic department from
    // the role string so the same lookup works for both HOD- and
    // agent-assigned rows.
    (agentRoster || []).forEach((a) => {
      const role = String(a.role || '').toLowerCase();
      let dept = '';
      if (role.includes('marketing')) dept = 'Marketing';
      else if (role.includes('sales') || role.includes('telesales')) dept = 'Sales';
      if (a?.id) m.set(a.id, { department: dept, role });
    });
    return m;
  }, [hodList, agentRoster]);

  // Narrows any "HOD-subject" record (hod, report, task, alert,
  // movement) to the currently-selected department. Tries the record's
  // own department fields first; if those are blank, falls back to the
  // department of the HOD it's assigned to. Tasks specifically only
  // carry `assigned_to` (the HOD's user_id) — without this fallback
  // they'd get filtered out entirely.
  const matchesSelectedDept = useCallback((row) => {
    if (!selectedDepartment || selectedDepartment === 'all') return true;
    let raw = (row?.department
            ?? row?.hod_department
            ?? row?.subject_department
            ?? row?.role
            ?? '').toString();
    if (!raw) {
      // Resolve via the HOD this row is associated with.
      const refId =
        row?.assigned_to ?? row?.hod_id ?? row?.agent_id ?? row?.subject_id;
      if (refId) {
        const hod = hodById.get(refId);
        if (hod) raw = hod.department || hod.role || '';
      }
    }
    raw = raw.toLowerCase().replace(/_/g, ' ');
    if (selectedDepartment === 'sales') return raw.includes('sales');
    if (selectedDepartment === 'marketing') return raw.includes('marketing');
    return true;
  }, [selectedDepartment, hodById]);

  // Department-scoped slices used in the tab trigger counts + table
  // rows. Declared AFTER the corresponding useState hooks above so
  // we don't trip the temporal dead zone. `visibleHodReports` is
  // declared further down because it also honours the in-tab
  // `hodTypeFilter`; both filters are AND-ed there.
  const visibleHods          = (hodList        || []).filter(matchesSelectedDept);
  const visibleHodTasks      = (hodTasks       || []).filter(matchesSelectedDept);
  const visibleHodAlerts     = (hodAlerts      || []).filter(matchesSelectedDept);
  const visibleHodFlagAlerts = (hodFlagAlerts  || []).filter(matchesSelectedDept);
  const visibleHodMovements  = (hodMovements   || []).filter(matchesSelectedDept);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  // Local state for the report-detail feedback box (text + sending flag).
  const [reportFeedbackText, setReportFeedbackText] = useState('');
  const [reportFeedbackSending, setReportFeedbackSending] = useState(false);
  // Common date filter for the whole HOD dashboard view. Empty strings
  // mean "no filter" — server defaults (this-week / today) apply.
  const todayStr = () => new Date().toISOString().slice(0, 10);
  const daysAgoStr = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
  const [filterFrom, setFilterFrom] = useState(daysAgoStr(7));
  const [filterTo, setFilterTo] = useState(todayStr());

  // Dedicated date filter for the Reports tab. Separate from the
  // page-wide filterFrom/filterTo so the CEO can scope the report feed
  // (e.g. "last 30 days") without disturbing the other tabs. Empty =
  // server default (last 7 days).
  const [reportsFrom, setReportsFrom] = useState('');
  const [reportsTo, setReportsTo] = useState('');
  const [reportsLoading, setReportsLoading] = useState(false);
  // HOD-type filter removed — the page-level violet "All Dashboard /
  // HOD Sales / HOD Marketing" pill now drives the same scoping
  // through `selectedDepartment`, so the duplicate in-tab chip was
  // pulled. `hodTypeFilter` is left below as a hard-coded 'all' so
  // any remaining reference (if a future component grafts on) keeps
  // working without a re-add.
  const hodTypeFilter = 'all';

  // Refetch just the Reports tab against /api/ceo/hods/reports with the
  // tab's own date range. Used by the Reports toolbar (refresh / clear /
  // date-picker changes) so the feed updates without reloading the
  // whole dashboard.
  const fetchHodReports = useCallback(async (from, to) => {
    if (!token) return;
    setReportsLoading(true);
    try {
      const p = new URLSearchParams();
      if (from) p.set('date_from', from);
      if (to) p.set('date_to', to);
      const qs = p.toString() ? `?${p.toString()}` : '';
      const res = await fetch(`${API_URL}/api/ceo/hods/reports${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setHodReports(data.data?.reports || []);
    } catch (e) {
      // non-fatal — keep whatever was already loaded
    } finally {
      setReportsLoading(false);
    }
  }, [token]);

  // Reports tab feed — narrowed by the page-level department
  // selector (`selectedDepartment`) only. The in-tab chip was
  // removed since it duplicated the header pill's behaviour.
  const visibleHodReports = (hodReports || []).filter(matchesSelectedDept);

  // Task states
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [newTaskModalOpen, setNewTaskModalOpen] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assigned_to: '',
    assigned_to_type: 'hod', // 'hod' or 'agent'
    priority: 'normal',
    due_date: ''
  });

  const availableViews = [
    { value: 'all', label: 'All Departments' },
    { value: 'sales', label: 'Sales' },
    { value: 'marketing', label: 'Marketing' }
  ];

  // Handle department change - notify parent
  const handleDepartmentChange = (value) => {
    if (onDepartmentChange) {
      onDepartmentChange(value);
    }
  };

  // Handle report click to view full report
  const handleViewReport = (report) => {
    setSelectedReport(report);
    setReportFeedbackText('');
    setReportModalOpen(true);
  };

  // Handle task click to view details
  const handleViewTask = (task) => {
    setSelectedTask(task);
    setTaskModalOpen(true);
  };

  // Fetch available users for task assignment — combines HODs and the
  // messageable-agents feed (both endpoints exist; the original
  // /api/admin/users was 404'ing, leaving the dropdown empty).
  const fetchAvailableUsers = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      const [hodsRes, agentsRes] = await Promise.all([
        fetch(`${API_URL}/api/ceo/hods`, { headers }),
        fetch(`${API_URL}/api/hod/messageable-agents`, { headers }),
      ]);
      const hodsJson = await hodsRes.json();
      const agentsJson = await agentsRes.json();
      // The /api/ceo/hods feed labels fields as hod_id / hod_name — map them
      // to the standard {id, full_name, role} shape the rest of the dialog
      // expects so the dropdown actually renders rows.
      const hods = (hodsJson?.data?.hods || []).map((h) => ({
        id: h.hod_id || h.id || h.user_id,
        full_name: h.hod_name || h.full_name || h.name || h.email,
        email: h.email,
        role: h.role || 'hod',
      }));
      const agents = (agentsJson?.data?.agents || []).map((a) => ({
        id: a.id || a.agent_id,
        full_name: a.full_name || a.agent_name || a.email,
        email: a.email,
        role: a.role || 'sales_agent',
      }));
      // Dedup by id in case the same user appears in both feeds.
      const seen = new Set();
      const merged = [...hods, ...agents].filter((u) => {
        if (!u.id || seen.has(u.id)) return false;
        seen.add(u.id);
        return true;
      });
      setAvailableUsers(merged);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  // Create new task
  const handleCreateTask = async () => {
    if (!newTask.title || !newTask.assigned_to) {
      toast.error('Please fill in title and assignee');
      return;
    }
    
    try {
      const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const response = await fetch(`${API_URL}/api/ceo/tasks`, {
        method: 'POST',
        headers,
        body: JSON.stringify(newTask)
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Task assigned successfully');
        setNewTaskModalOpen(false);
        setNewTask({
          title: '',
          description: '',
          assigned_to: '',
          assigned_to_type: 'hod',
          priority: 'normal',
          due_date: ''
        });
        // Refresh tasks
        const tasksRes = await fetch(`${API_URL}/api/ceo/hods/tasks`, { headers: { 'Authorization': `Bearer ${token}` } });
        const tasksData = await tasksRes.json();
        if (tasksData.success) setHodTasks(tasksData.data?.tasks || []);
      } else {
        toast.error(data.detail || 'Failed to create task');
      }
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to create task');
    }
  };

  // Fetch users when new task modal opens
  useEffect(() => {
    if (newTaskModalOpen && token) {
      fetchAvailableUsers();
    }
  }, [newTaskModalOpen, token]);

  useEffect(() => {
    const fetchAllHODData = async () => {
      if (!token) return;
      const headers = { 'Authorization': `Bearer ${token}` };
      // Build a shared ?date_from=&date_to= suffix the endpoints can use
      // when they support it. Endpoints that don't read these params just
      // ignore them — the filter is best-effort wherever it applies.
      const qs = (() => {
        const p = new URLSearchParams();
        if (filterFrom) p.set('date_from', filterFrom);
        if (filterTo) p.set('date_to', filterTo);
        const s = p.toString();
        return s ? `?${s}` : '';
      })();

      // Progressive fetch — each endpoint runs independently and applies
      // its data the moment it lands, so a slow endpoint (some of these
      // take 5-12s) never blocks the others. The page stops showing
      // "Loading HOD Dashboard…" as soon as the overview arrives rather
      // than waiting for the slowest call.
      const pull = async (url, apply) => {
        try {
          const res = await fetch(url, { headers });
          const data = await res.json();
          if (data && data.success) apply(data);
        } catch (e) {
          // non-fatal — that one panel just stays on its own empty state
        }
      };

      const tasks = [
        pull(`${API_URL}/api/hod/overview${qs}`, (d) => { setHodData(d.data); setLoading(false); }),
        pull(`${API_URL}/api/ceo/hods${qs}`, (d) => setHodList(d.data?.hods || [])),
        pull(`${API_URL}/api/ceo/hods/reports${qs}`, (d) => setHodReports(d.data?.reports || [])),
        pull(`${API_URL}/api/ceo/hods/tasks${qs}`, (d) => setHodTasks(d.data?.tasks || [])),
        pull(`${API_URL}/api/ceo/hods/alerts${qs}`, (d) => setHodAlerts(d.data?.alerts || [])),
        pull(`${API_URL}/api/ceo/hods/movements${qs}`, (d) => setHodMovements(d.data?.movements || [])),
        // Per-HOD Alerts & Flags — /api/hod/alerts admits the CEO and
        // stamps subject_class on each alert so we keep the HOD ones.
        pull(`${API_URL}/api/hod/alerts`, (d) =>
          setHodFlagAlerts((d.data?.alerts || []).filter((a) => a.subject_class === 'hod'))),
      ];

      // Safety net — never leave the page stuck on the spinner even if
      // the overview call is slow or fails.
      const safety = setTimeout(() => setLoading(false), 4000);
      Promise.allSettled(tasks).finally(() => {
        clearTimeout(safety);
        setLoading(false);
      });
    };
    fetchAllHODData();
  }, [token, filterFrom, filterTo]);

  // Sales-specific metrics
  const salesMetrics = [
    {
      label: 'Monthly Revenue',
      value: <><DirhamSign /> {(hodData?.revenue?.this_month || 0).toLocaleString()}</>,
      subtext: `${hodData?.revenue?.deals_closed || 0} deals closed`,
      // Dirham mark in the icon pill — matches the currency on the value.
      icon: DirhamSign,
      color: 'text-emerald-500',
      bgColor: darkMode ? 'bg-emerald-900/20' : 'bg-emerald-50'
    },
    {
      label: 'Active Deals',
      value: Object.values(hodData?.deals_by_stage || {}).reduce((acc, s) => acc + (s.count || 0), 0),
      subtext: <><DirhamSign /> {Object.values(hodData?.deals_by_stage || {}).reduce((acc, s) => acc + (s.value || 0), 0).toLocaleString()} pipeline</>,
      icon: Target,
      color: 'text-blue-500',
      bgColor: darkMode ? 'bg-blue-900/20' : 'bg-blue-50'
    },
    {
      label: 'New Leads',
      value: Object.values(hodData?.leads_by_source || {}).reduce((acc, c) => acc + c, 0),
      subtext: `${Object.keys(hodData?.leads_by_source || {}).length} sources`,
      icon: Users,
      color: 'text-purple-500',
      bgColor: darkMode ? 'bg-purple-900/20' : 'bg-purple-50'
    },
    {
      label: 'Units Sold',
      value: hodData?.units?.sold || 0,
      subtext: `${hodData?.units?.available || 0} available`,
      icon: Building,
      color: 'text-amber-500',
      bgColor: darkMode ? 'bg-amber-900/20' : 'bg-amber-50'
    }
  ];

  // Marketing-specific metrics
  const marketingMetrics = [
    {
      label: 'Campaign Reach',
      value: '12.5K',
      subtext: '+18% from last month',
      icon: TrendingUp,
      color: 'text-purple-500',
      bgColor: darkMode ? 'bg-purple-900/20' : 'bg-purple-50'
    },
    {
      label: 'Lead Conversions',
      value: '342',
      subtext: '4.2% conversion rate',
      icon: Target,
      color: 'text-emerald-500',
      bgColor: darkMode ? 'bg-emerald-900/20' : 'bg-emerald-50'
    },
    {
      label: 'Ad Spend',
      value: <><DirhamSign /> 8,450</>,
      subtext: <>Cost per lead: <DirhamSign /> 24.70</>,
      icon: DirhamSign,
      color: 'text-blue-500',
      bgColor: darkMode ? 'bg-blue-900/20' : 'bg-blue-50'
    },
    {
      label: 'Email Open Rate',
      value: '28.5%',
      subtext: '15,200 emails sent',
      icon: MessageSquare,
      color: 'text-amber-500',
      bgColor: darkMode ? 'bg-amber-900/20' : 'bg-amber-50'
    }
  ];

  const metrics = selectedDepartment === 'marketing' ? marketingMetrics : salesMetrics;

  if (loading) {
    return (
      <Card className={darkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}>
        <CardContent className="p-8">
          <div className="flex items-center justify-center">
            <RefreshCw className="animate-spin text-violet-500" size={32} />
            <span className={`ml-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Loading HOD Dashboard...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={darkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className={`text-xl flex items-center gap-2 flex-wrap ${darkMode ? 'text-white' : ''}`}>
              <Activity size={24} className="text-blue-500" />
              HOD Dashboard
              <HelpInfo
                align="inline"
                title="How to use the embedded HOD Dashboard"
                steps={[
                  'This panel mirrors what your HODs see — but for every department at once. Use the Sales / Marketing / All Departments dropdown to focus.',
                  <>The date filter at the top (7d / 30d / custom range) flows into every tab below: <em>HODs</em>, <em>Reports</em>, <em>Tasks</em>, <em>In & Out</em>, <em>Alerts</em>.</>,
                  <>The <strong>HODs</strong> tab lists each department head with team size, today\'s active count, this week\'s deals, and pending reports.</>,
                  <>The <strong>Reports</strong> tab merges HOD + Agent daily reports + site-visit reports. Click any row to read the full report and send feedback to the author.</>,
                  <>The <strong>Tasks</strong> tab shows directives you\'ve assigned to HODs or agents. Click Assign New Task to create one.</>,
                  <>The <strong>In & Out</strong> tab is HOD field movements with site-visit verification.</>,
                  <>The <strong>Alerts</strong> tab is org-wide flags — overdue follow-ups, stuck deals, no-activity agents. Each carries a Send Feedback / Send Warning quick action.</>,
                ]}
                tip="Start your morning on the Alerts tab — it surfaces the day\'s decisions in one place."
              />
              {selectedDepartment !== 'all' && (
                <Badge className={selectedDepartment === 'sales' ? 'bg-blue-500' : 'bg-purple-500'}>
                  {selectedDepartment === 'sales' ? 'Sales' : 'Marketing'}
                </Badge>
              )}
            </CardTitle>
            <CardDescription className={darkMode ? 'text-slate-400' : ''}>
              Complete department performance overview with team management
            </CardDescription>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Common date filter — applied to all tabs (Overview, HODs,
                Reports, Tasks, In & Out, Alerts). Wherever an endpoint
                accepts date_from / date_to it will narrow accordingly. */}
            <div className="flex items-center gap-1.5">
              <Calendar size={14} className={darkMode ? 'text-slate-400' : 'text-slate-500'} />
              <input
                type="date"
                value={filterFrom}
                onChange={(e) => setFilterFrom(e.target.value)}
                className={`h-9 px-2 text-xs rounded border ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : 'border-slate-300'}`}
                title="From"
              />
              <span className="text-xs text-slate-400">to</span>
              <input
                type="date"
                value={filterTo}
                onChange={(e) => setFilterTo(e.target.value)}
                className={`h-9 px-2 text-xs rounded border ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : 'border-slate-300'}`}
                title="To"
              />
              <Button
                variant="ghost"
                size="sm"
                className="h-9 text-xs"
                onClick={() => { setFilterFrom(daysAgoStr(7)); setFilterTo(todayStr()); }}
              >
                7d
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 text-xs"
                onClick={() => { setFilterFrom(daysAgoStr(30)); setFilterTo(todayStr()); }}
              >
                30d
              </Button>
              {/* Always visible so the affordance is obvious — dimmed
                  when both dates are already empty (i.e. nothing to
                  clear). The button stays clickable as a safety net. */}
              <Button
                variant="ghost"
                size="sm"
                className={`h-9 text-xs ${
                  (filterFrom || filterTo)
                    ? 'text-rose-600 hover:text-rose-700'
                    : 'text-slate-400 hover:text-rose-600'
                }`}
                onClick={() => { setFilterFrom(''); setFilterTo(''); }}
                title="Clear date filter — show all-time"
              >
                Clear
              </Button>
            </div>
            {/* Violet "All Departments" filter removed per request.
                The CEO drives the department from the violet "All
                Dashboard / HOD Sales / HOD Marketing / Agents
                Dashboard Overview" pill in the page header above —
                that pill already maps to `selectedDepartment` via
                the dashboardScope useEffect, so removing this duplicate
                in-panel filter has no behavioural impact, just less
                visual noise inside the embedded HOD view. */}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* Metrics Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {metrics.map((metric, idx) => (
            <div 
              key={idx} 
              className={`p-4 rounded-lg border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className={`text-sm font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {metric.label}
                  </p>
                  <p className={`text-2xl font-bold mt-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                    {metric.value}
                  </p>
                  <p className={`text-xs mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {metric.subtext}
                  </p>
                </div>
                <div className={`p-2 rounded-lg ${metric.bgColor}`}>
                  <metric.icon className={metric.color} size={20} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="pt-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className={darkMode ? 'bg-slate-800' : ''}>
              <TabsTrigger value="overview" className="flex items-center gap-2">
                <BarChart3 size={16} />
                Overview
              </TabsTrigger>
              <TabsTrigger value="hods" className="flex items-center gap-2">
                <Crown size={16} />
                HODs ({visibleHods.length})
              </TabsTrigger>
              {/* Agents tab — mirrors the standalone HOD dashboard's
                  Agents tab. Surfaces the per-agent Live Status grid
                  here so the CEO doesn't have to leave this view to
                  see individual agent stats. Filtered to the
                  selected department through the AgentStatusDashboard's
                  own `departmentFilter` prop. */}
              <TabsTrigger value="agents" className="flex items-center gap-2">
                <Users size={16} />
                Agents ({(agentRoster || []).filter((a) => {
                  if (!selectedDepartment || selectedDepartment === 'all') return true;
                  const r = String(a.role || '').toLowerCase();
                  return selectedDepartment === 'sales'
                    ? (r.includes('sales') || r.includes('telesales'))
                    : r.includes('marketing');
                }).length})
              </TabsTrigger>
              <TabsTrigger value="reports" className="flex items-center gap-2">
                <FileText size={16} />
                Reports ({visibleHodReports.length})
              </TabsTrigger>
              <TabsTrigger value="tasks" className="flex items-center gap-2">
                <Activity size={16} />
                Tasks ({visibleHodTasks.length})
              </TabsTrigger>
              {/* In & Out — field-attendance tracking is a sales /
                  operations concern (agents going on site visits).
                  Marketing HODs don't go on site visits, so the tab
                  is hidden when the user is scoped to the Marketing
                  department. */}
              {selectedDepartment !== 'marketing' && (
                <TabsTrigger value="movements" className="flex items-center gap-2">
                  <MapPin size={16} />
                  In & Out ({visibleHodMovements.length})
                </TabsTrigger>
              )}
              <TabsTrigger value="alerts" className="flex items-center gap-2">
                <AlertTriangle size={16} />
                Alerts ({visibleHodFlagAlerts.length})
              </TabsTrigger>
              {/* CEO Comments tab — mirrors the standalone HOD's
                  CEO Comments tab so the comment thread is reachable
                  without leaving the embedded HOD view. The red badge
                  carries the unread-by-HOD count so the CEO sees how
                  many of their messages haven't been read yet —
                  matches the badge styling the HOD sees on their own
                  page for the receiving side. */}
              <TabsTrigger value="comments" className="flex items-center gap-2">
                <MessageSquare size={16} />
                CEO Comments
                {(() => {
                  const unread = (comments || []).filter((c) => !c.is_read_by_hod).length;
                  return unread > 0 ? (
                    <Badge variant="destructive" className="ml-1 text-[10px] px-1.5 py-0">
                      {unread}
                    </Badge>
                  ) : null;
                })()}
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab — mirrors the standalone HOD dashboard's
                Overview, which leads with Live Agent Status. The
                Pipeline by Stage + Leads by Source charts that used
                to dominate this tab now sit below the agent grid so
                a CEO scoping to HOD Sales / Marketing sees the same
                "who's working right now" snapshot the HOD sees
                first. */}
            <TabsContent value="overview" className="mt-6 space-y-6" forceMount={true} hidden={activeTab !== 'overview'}>
              {mountedTabs.has('overview') && (
              <>
              {/* Executive Briefing — replaces the old Tab Summary
                  grid. The tab strip at the top already carries the
                  per-tab counts, so duplicating them here was noise.
                  This section is a narrative "what to do next" — three
                  action-oriented cards keyed off severity signals in
                  the existing data (Attention / Wins / Watch). Each
                  jumps to the relevant tab or list. */}
              {(() => {
                const unreadComments = (comments || []).filter((c) => !c.is_read_by_hod).length;
                const urgentComments = (comments || []).filter((c) => c.priority === 'urgent');
                const urgentAlerts   = (visibleHodFlagAlerts || []).filter(
                  (a) => a.priority === 'urgent' || a.severity === 'critical',
                );
                // Freshest today (report / task) — proxy for momentum.
                const todayIso = new Date().toISOString().slice(0, 10);
                const reportsToday = (visibleHodReports || []).filter((r) =>
                  String(r.date || r.created_at || '').startsWith(todayIso),
                ).length;
                const tasksOpen = (visibleHodTasks || []).filter(
                  (t) => (t.status || '').toLowerCase() !== 'completed',
                ).length;

                // --- Card 1: ATTENTION ------------------------------
                let attentionTitle, attentionBody, attentionAction, attentionTab;
                if (urgentComments.length > 0) {
                  const first = urgentComments[0];
                  attentionTitle = `${urgentComments.length} urgent CEO comment${urgentComments.length === 1 ? '' : 's'}`;
                  attentionBody = first.title || first.message || 'Unread urgent thread from an HOD.';
                  attentionAction = 'Open CEO Comments';
                  attentionTab = 'comments';
                } else if (urgentAlerts.length > 0) {
                  const first = urgentAlerts[0];
                  attentionTitle = `${urgentAlerts.length} critical alert${urgentAlerts.length === 1 ? '' : 's'}`;
                  attentionBody = first.title || first.description || 'One or more flags need CEO review.';
                  attentionAction = 'Review Alerts';
                  attentionTab = 'alerts';
                } else if (unreadComments > 0) {
                  attentionTitle = `${unreadComments} unread comment${unreadComments === 1 ? '' : 's'}`;
                  attentionBody = 'HODs are waiting on your reply.';
                  attentionAction = 'Open CEO Comments';
                  attentionTab = 'comments';
                } else {
                  attentionTitle = 'Nothing urgent';
                  attentionBody = 'No unresolved alerts or unread comments. You are caught up.';
                  attentionAction = 'View Alerts';
                  attentionTab = 'alerts';
                }

                // --- Card 2: MOMENTUM (wins today) ------------------
                let momentumTitle, momentumBody, momentumAction, momentumTab;
                if (reportsToday > 0) {
                  momentumTitle = `${reportsToday} report${reportsToday === 1 ? '' : 's'} filed today`;
                  momentumBody = 'Daily + site-visit reports from HODs. Fresh field intelligence.';
                  momentumAction = 'Open Reports';
                  momentumTab = 'reports';
                } else if ((visibleHodReports || []).length > 0) {
                  momentumTitle = `${visibleHodReports.length} report${visibleHodReports.length === 1 ? '' : 's'} on record`;
                  momentumBody = 'No new report today yet. Reach out if you need a pulse check.';
                  momentumAction = 'Open Reports';
                  momentumTab = 'reports';
                } else {
                  momentumTitle = 'No reports yet';
                  momentumBody = 'Ask HODs to file their daily update.';
                  momentumAction = 'Open Reports';
                  momentumTab = 'reports';
                }

                // --- Card 3: DELIVERY (tasks open) ------------------
                let deliveryTitle, deliveryBody, deliveryAction, deliveryTab;
                if (tasksOpen > 0) {
                  deliveryTitle = `${tasksOpen} open task${tasksOpen === 1 ? '' : 's'} out to HODs`;
                  deliveryBody = 'Assigned by you; not yet marked done.';
                  deliveryAction = 'Open Tasks';
                  deliveryTab = 'tasks';
                } else if ((visibleHodTasks || []).length > 0) {
                  deliveryTitle = 'All tasks completed';
                  deliveryBody = `${visibleHodTasks.length} assigned this cycle — every one closed.`;
                  deliveryAction = 'Assign Next';
                  deliveryTab = 'tasks';
                } else {
                  deliveryTitle = 'No tasks assigned';
                  deliveryBody = 'Use the Assign Task pill in the header to delegate.';
                  deliveryAction = 'Open Tasks';
                  deliveryTab = 'tasks';
                }

                const briefingCards = [
                  {
                    key: 'attention', icon: AlertTriangle,
                    accent: (urgentComments.length + urgentAlerts.length + unreadComments) > 0 ? 'red' : 'emerald',
                    kicker: 'Needs Attention',
                    title: attentionTitle,
                    body:  attentionBody,
                    action: attentionAction,
                    tab: attentionTab,
                  },
                  {
                    key: 'momentum', icon: FileText, accent: 'blue',
                    kicker: 'Momentum',
                    title: momentumTitle,
                    body: momentumBody,
                    action: momentumAction,
                    tab: momentumTab,
                  },
                  {
                    key: 'delivery', icon: Activity, accent: 'violet',
                    kicker: 'Delivery',
                    title: deliveryTitle,
                    body: deliveryBody,
                    action: deliveryAction,
                    tab: deliveryTab,
                  },
                ];
                const briefingAccentIcon = {
                  red:     'bg-red-50 text-red-600 border-red-200',
                  emerald: 'bg-emerald-50 text-emerald-600 border-emerald-200',
                  blue:    'bg-blue-50 text-blue-600 border-blue-200',
                  violet:  'bg-violet-50 text-violet-600 border-violet-200',
                };
                const briefingBorder = {
                  red:     'border-red-200 bg-red-50/30',
                  emerald: 'border-emerald-200 bg-emerald-50/30',
                  blue:    'border-blue-200 bg-blue-50/20',
                  violet:  'border-violet-200 bg-violet-50/20',
                };
                return (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <BarChart3 size={18} className={darkMode ? 'text-slate-400' : 'text-slate-500'} />
                      <h4 className={`font-medium text-base ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        Today's Briefing
                      </h4>
                      <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        — what to act on next
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {briefingCards.map((c) => {
                        const Icon = c.icon;
                        return (
                          <div
                            key={c.key}
                            className={`group p-5 rounded-xl border transition ${
                              darkMode
                                ? 'border-slate-700 bg-slate-800/40'
                                : briefingBorder[c.accent]
                            }`}
                            data-testid={`briefing-${c.key}`}
                          >
                            <div className="flex items-center gap-3 mb-3">
                              <div className={`w-9 h-9 rounded-lg flex items-center justify-center border ${briefingAccentIcon[c.accent]}`}>
                                <Icon size={15} />
                              </div>
                              <p className={`text-[10px] uppercase tracking-widest font-bold ${
                                darkMode ? 'text-slate-400' : 'text-slate-500'
                              }`}>{c.kicker}</p>
                            </div>
                            <p className={`text-base font-semibold leading-snug ${
                              darkMode ? 'text-white' : 'text-slate-900'
                            }`}>{c.title}</p>
                            <p className={`text-xs mt-1.5 leading-relaxed ${
                              darkMode ? 'text-slate-400' : 'text-slate-600'
                            }`}>{c.body}</p>
                            <button
                              type="button"
                              onClick={() => setActiveTab(c.tab)}
                              className={`mt-4 text-xs font-semibold inline-flex items-center gap-1 ${
                                darkMode ? 'text-slate-300 hover:text-white' : 'text-slate-700 hover:text-slate-900'
                              }`}
                            >
                              {c.action}
                              <span aria-hidden>→</span>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              {/* Live Agent Status / geofence dashboard intentionally
                  removed from the Overview tab — it lives on the
                  dedicated Agents tab to avoid duplicating the same
                  panel on two tabs. The Overview now stays focused on
                  the Tab Summary above + Pipeline / Leads below. */}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pipeline by Stage */}
              <div className={`p-4 rounded-lg border ${darkMode ? 'bg-slate-800/30 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                <h4 className={`font-medium mb-3 flex items-center gap-2 flex-wrap ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  Pipeline by Stage
                  <HelpInfo
                    align="inline"
                    title="How to read Pipeline by Stage"
                    steps={[
                      'Each row is a deal stage — Lead, Qualified, Negotiation, Proposal — with how many open deals sit there and their combined value.',
                      <>The value badge is the total worth of deals in that stage, not closed revenue. It answers "how much is riding on this stage right now?"</>,
                      'A healthy pipeline tapers: lots in early stages, fewer (but larger) deeper in. A bulge stuck in one stage points to a bottleneck there.',
                      'These numbers roll up the whole company across both Sales departments — use the HODs tab to see who owns what.',
                    ]}
                    tip="Watch the Negotiation and Proposal totals week over week — if value parks there without closing, it's a forecasting risk, not progress."
                  />
                </h4>
                <div className="space-y-2">
                  {Object.entries(hodData?.deals_by_stage || {}).map(([stage, data]) => (
                    <div key={stage} className="flex items-center justify-between">
                      <span className={`text-sm capitalize ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {stage.replace(/_/g, ' ')}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                          {data.count} deals
                        </span>
                        <Badge variant="outline"><DirhamSign /> {(data.value || 0).toLocaleString()}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Leads by Source */}
              <div className={`p-4 rounded-lg border ${darkMode ? 'bg-slate-800/30 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className={`font-medium flex items-center gap-2 flex-wrap ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                    Leads by Source
                    <HelpInfo
                      align="inline"
                      title="How to read Leads by Source"
                      steps={[
                        'Each row is a lead channel — LinkedIn, Email Campaign, Cold Call, Website, Trade Show — with how many leads it brought in.',
                        <>The badge on the right (<em>all time</em> / <em>this month</em>) tells you which window the counts cover.</>,
                        'Ranked highest-first, so the channel at the top is your strongest lead generator for that window.',
                        'Pair this with conversion: a channel with high lead volume but few closed deals is cheap traffic, not quality — worth rebalancing spend.',
                      ]}
                      tip="A heavily lopsided list (one source dominating) is a concentration risk — if that channel dries up, the funnel does too."
                    />
                  </h4>
                  <Badge variant="outline" className="text-xs">
                    {hodData?.leads_window === 'all_time' ? 'all time' : 'this month'}
                  </Badge>
                </div>
                <div className="space-y-2">
                  {Object.keys(hodData?.leads_by_source || {}).length === 0 && (
                    <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      No leads recorded yet.
                    </p>
                  )}
                  {Object.entries(hodData?.leads_by_source || {}).map(([source, count]) => (
                    <div key={source} className="flex items-center justify-between">
                      <span className={`text-sm capitalize ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                        {source.replace(/_/g, ' ')}
                      </span>
                      <span className={`text-sm font-bold ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                        {count}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
              </>
              )}
          </TabsContent>

          {/* HODs Tab */}
          <TabsContent value="hods" className="mt-6" forceMount={true} hidden={activeTab !== 'hods'}>
            {mountedTabs.has('hods') && (
            <div className="overflow-x-auto">
              <div className="flex items-center gap-2 mb-2">
                <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  {deptLabel ? `${deptLabel} Heads of Department` : 'Heads of Department'}
                </h4>
                <HelpInfo
                  align="inline"
                  title="How to read the HODs tab"
                  steps={[
                    'One row per Head of Department, with their department, team size, how many of their agents were active today, deals closed this week (with pipeline value), and pending report count.',
                    <>The <strong>Active Today</strong> column is a quick health read — <em>0 / 17</em> in red means none of that HOD\'s team has logged activity yet.</>,
                    <><strong>Deals (Week)</strong> shows count and total value closed by the whole department this week.</>,
                    <><strong>Pending Reports</strong> turns amber when an HOD has reports awaiting their review; "All reviewed" means they\'re caught up.</>,
                  ]}
                  tip="A department with a big team but low Active Today and few weekly deals is where to focus a 1:1 with that HOD."
                />
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className={`border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <th className={`text-left py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>HOD</th>
                    <th className={`text-center py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Department</th>
                    <th className={`text-center py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Team Size</th>
                    <th className={`text-center py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Active Today</th>
                    <th className={`text-center py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Deals (Week)</th>
                    <th className={`text-center py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Pending Reports</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleHods.length > 0 ? visibleHods.map((hod) => (
                    <tr key={hod.hod_id} className={`border-b ${darkMode ? 'border-slate-800' : 'border-slate-100'}`}>
                      <td className={`py-3 px-3 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        <div className="flex items-center gap-2">
                          <Crown size={16} className="text-amber-500" />
                          {hod.hod_name}
                        </div>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <Badge className={hod.department === 'Sales' ? 'bg-blue-500' : 'bg-purple-500'}>
                          {hod.department}
                        </Badge>
                      </td>
                      <td className={`py-3 px-3 text-center ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        {hod.team_count} agents
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className={`${hod.active_today > 0 ? 'text-emerald-500' : 'text-red-500'} font-medium`}>
                          {hod.active_today} / {hod.team_count}
                        </span>
                      </td>
                      <td className={`py-3 px-3 text-center font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {hod.deals_week} (<DirhamSign /> {(hod.deals_value || 0).toLocaleString()})
                      </td>
                      <td className="py-3 px-3 text-center">
                        {hod.pending_reports > 0 ? (
                          <Badge variant="outline" className="border-amber-500 text-amber-500">
                            {hod.pending_reports} pending
                          </Badge>
                        ) : (
                          <span className="text-emerald-500">All reviewed</span>
                        )}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={6} className={`py-8 text-center ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        No HODs found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            )}
          </TabsContent>

          {/* Agents Tab — Live Agent Status grid, scoped to the
              selected department. Re-uses the same component the
              standalone HOD dashboard renders so the CEO sees the
              identical per-agent card layout. */}
          <TabsContent value="agents" className="mt-6" forceMount={true} hidden={activeTab !== 'agents'}>
            {mountedTabs.has('agents') && (
              <AgentStatusDashboard
                compact={false}
                departmentFilter={selectedDepartment === 'sales' ? 'sales'
                                : selectedDepartment === 'marketing' ? 'marketing'
                                : 'all'}
              />
            )}
          </TabsContent>

          {/* Reports Tab - HOD Submitted Daily Reports */}
          <TabsContent value="reports" className="mt-6" forceMount={true} hidden={activeTab !== 'reports'}>
            {mountedTabs.has('reports') && (
            <>
            <div className="flex items-center gap-2 mb-1">
              <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {deptLabel ? `${deptLabel} HOD Reports` : 'HOD Reports'}
              </h4>
              <HelpInfo
                align="inline"
                title="How to use the Reports tab"
                steps={[
                  'Daily Reports and Site Visit Reports filed by your HODs. Agent-level reports stay on each HOD\'s own dashboard.',
                  <>The feed is automatically scoped by the page-level <strong>All Dashboard / HOD Sales / HOD Marketing</strong> pill in the header.</>,
                  <>Use the <strong>date range</strong> to set the window; <strong>Clear</strong> empties the dates (last 7 days is the default), and the refresh icon re-pulls.</>,
                  'Each row shows the HOD, department, report type and a summary. Click a row to open the full report.',
                ]}
                tip="Switch the header pill to HOD Sales / HOD Marketing before a department review — you get just that side's reports in one place."
              />
            </div>
            <p className={`text-xs mb-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              {selectedDepartment === 'sales'
                ? 'Daily Report and Site Visit Report from Sales HODs'
                : selectedDepartment === 'marketing'
                  ? 'Daily Report and Site Visit Report from Marketing HODs'
                  : 'Daily Report and Site Visit Report'}
            </p>
            {/* Reports toolbar — date range + refresh + clear. The
                old in-tab "Sales HOD / Marketing HOD" select was
                removed because the page-level violet pill above
                already drives the same filter through
                `selectedDepartment`. */}
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <Calendar size={14} className={darkMode ? 'text-slate-400' : 'text-slate-500'} />
              <input
                type="date"
                value={reportsFrom}
                onChange={(e) => { setReportsFrom(e.target.value); fetchHodReports(e.target.value, reportsTo); }}
                className={`h-8 px-2 text-xs rounded border ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : 'border-slate-300'}`}
                title="Reports from date"
              />
              <span className="text-xs text-slate-400">to</span>
              <input
                type="date"
                value={reportsTo}
                onChange={(e) => { setReportsTo(e.target.value); fetchHodReports(reportsFrom, e.target.value); }}
                className={`h-8 px-2 text-xs rounded border ${darkMode ? 'bg-slate-800 border-slate-600 text-white' : 'border-slate-300'}`}
                title="Reports to date"
              />
              {/* Clear — always visible; dims when there is nothing to
                  clear. Empties both dates and reloads (server then
                  falls back to last 7 days). */}
              <Button
                variant="ghost"
                size="sm"
                className={`h-8 text-xs ${
                  (reportsFrom || reportsTo)
                    ? 'text-rose-600 hover:text-rose-700'
                    : 'text-slate-400 hover:text-rose-600'
                }`}
                onClick={() => { setReportsFrom(''); setReportsTo(''); fetchHodReports('', ''); }}
                title="Clear the reports date filter"
              >
                Clear
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => fetchHodReports(reportsFrom, reportsTo)}
                title="Refresh reports"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${reportsLoading ? 'animate-spin' : ''}`} />
              </Button>
              <span className={`ml-auto text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {visibleHodReports.length} report{visibleHodReports.length === 1 ? '' : 's'}
              </span>
            </div>
            <ScrollArea className="h-[300px]">
              <div className="space-y-3">
                {visibleHodReports.length > 0 ? visibleHodReports.map((report) => (
                  <div 
                    key={report.id} 
                    className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                      darkMode 
                        ? 'bg-slate-800/30 border-slate-700 hover:bg-slate-800/50 hover:border-violet-500/50' 
                        : 'bg-slate-50 border-slate-200 hover:bg-white hover:border-violet-300'
                    }`}
                    onClick={() => handleViewReport(report)}
                    data-testid={`hod-report-${report.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <FileText size={16} className={
                            report.type === 'site_visit_report' ? 'text-emerald-500'
                              : report.type === 'agent_daily_report' ? 'text-blue-500'
                              : 'text-violet-500'
                          } />
                          <p className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                            {report.hod_name}
                          </p>
                          <Badge className={report.department === 'Sales' ? 'bg-blue-500' : report.department === 'Marketing' ? 'bg-purple-500' : 'bg-slate-500'}>
                            {report.department}
                          </Badge>
                          {/* Report-type badge so the CEO can tell HOD daily,
                              agent daily and site-visit reports apart. */}
                          {report.type === 'site_visit_report' ? (
                            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300 text-xs">
                              Site Visit{report.property_name ? ` · ${report.property_name}` : ''}
                            </Badge>
                          ) : report.type === 'agent_daily_report' ? (
                            <Badge className="bg-blue-100 text-blue-700 border border-blue-300 text-xs">
                              Agent Daily
                            </Badge>
                          ) : (
                            <Badge className="bg-violet-100 text-violet-700 border border-violet-300 text-xs">
                              HOD Daily
                            </Badge>
                          )}
                          {report.version > 1 && (
                            <Badge variant="outline" className="text-xs">v{report.version}</Badge>
                          )}
                        </div>
                        <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {report.summary}
                        </p>
                        <div className="flex items-center gap-3 mt-2">
                          <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'} flex items-center gap-1`}>
                            <Calendar size={12} /> 
                            Report Date: {report.report_date || 'N/A'}
                          </span>
                          <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'} flex items-center gap-1`}>
                            <Zap size={12} /> 
                            {report.trigger_type === 'activity_save' ? 'Auto-generated' : 'Manual'}
                          </span>
                          <span className="text-xs text-violet-500 flex items-center gap-1">
                            <Eye size={12} /> Click to view
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge variant="outline">
                          {report.submitted_at ? new Date(report.submitted_at).toLocaleDateString() : 'Recent'}
                        </Badge>
                        <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                          {report.submitted_at ? new Date(report.submitted_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className={`text-center py-8 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    <FileText size={32} className="mx-auto mb-2 text-slate-400" />
                    No HOD reports submitted yet
                  </div>
                )}
              </div>
            </ScrollArea>
            </>
            )}
          </TabsContent>

          {/* Tasks Tab — CEO directives assigned to HODs or Agents */}
          <TabsContent value="tasks" className="mt-6" forceMount={true} hidden={activeTab !== 'tasks'}>
            {mountedTabs.has('tasks') && (
            <>
            <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
              <div>
                <h4 className={`font-medium flex items-center gap-2 flex-wrap ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  {deptLabel ? `${deptLabel} Tasks Assigned by CEO` : 'Tasks Assigned by CEO'}
                  <HelpInfo
                    align="inline"
                    title="How to use the Tasks tab"
                    steps={[
                      'Every directive you\'ve assigned from the CEO level — to an HOD or directly to an agent — lands here with its status, priority and due date.',
                      <>Click <strong>New Task</strong> to create one: pick the recipient, set a priority and due date; they get a bell notification and the task shows on their dashboard.</>,
                      'Status moves as the work progresses (Open → In progress → Done); overdue tasks are flagged so they stand out.',
                      'The count on the tab is the number of open directives still in play.',
                    ]}
                    tip="Keep CEO tasks few and high-leverage — they cut through the org. Day-to-day follow-ups belong with the HODs."
                  />
                </h4>
                <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Executive directives flowing from CEO to HODs and Agents
                </p>
              </div>
              <Button
                onClick={() => setNewTaskModalOpen(true)}
                className="bg-violet-600 hover:bg-violet-700"
                size="sm"
              >
                <Plus size={16} className="mr-1" />
                Assign New Task
              </Button>
            </div>
            
            <ScrollArea className="h-[260px]">
              <div className="space-y-3">
                {visibleHodTasks.length > 0 ? visibleHodTasks.map((task) => (
                  <div 
                    key={task.id} 
                    className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                      darkMode 
                        ? 'bg-slate-800/30 border-slate-700 hover:bg-slate-800/50 hover:border-violet-500/50' 
                        : 'bg-slate-50 border-slate-200 hover:bg-white hover:border-violet-300'
                    }`}
                    onClick={() => handleViewTask(task)}
                    data-testid={`hod-task-${task.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                          {task.title}
                        </p>
                        <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          Assigned by: <span className="text-violet-500">{task.assigned_by_name}</span>
                        </p>
                        {task.assigned_to_name && (
                          <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            To: {task.assigned_to_name}
                            {task.assigned_to_type && (
                              <span className={`ml-1 uppercase text-[10px] tracking-wide ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                ({task.assigned_to_type})
                              </span>
                            )}
                          </p>
                        )}
                        <div className={`flex items-center gap-3 mt-2 text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {task.created_at && (
                            <span className="flex items-center gap-1 font-mono">
                              <Calendar size={11} />
                              Assigned {new Date(task.created_at).toLocaleString(undefined, {
                                year: 'numeric', month: 'short', day: '2-digit',
                                hour: '2-digit', minute: '2-digit',
                              })}
                            </span>
                          )}
                          {task.due_date && (
                            <span className="flex items-center gap-1 font-mono text-amber-600">
                              <Clock size={11} />
                              Due {new Date(task.due_date).toLocaleString(undefined, {
                                year: 'numeric', month: 'short', day: '2-digit',
                                hour: '2-digit', minute: '2-digit',
                              })}
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-violet-500 flex items-center gap-1 mt-1">
                          <Eye size={12} /> Click to view
                        </span>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge variant={task.status === 'completed' ? 'default' : task.status === 'in_progress' ? 'secondary' : 'outline'}
                               className={task.status === 'completed' ? 'bg-emerald-500' : ''}>
                          {task.status?.replace(/_/g, ' ')}
                        </Badge>
                        {task.priority === 'urgent' && (
                          <Badge variant="destructive" className="text-xs">Urgent</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className={`text-center py-8 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    No HOD tasks assigned yet
                  </div>
                )}
              </div>
            </ScrollArea>
            </>
            )}
          </TabsContent>

          {/* Movements Tab - HOD In & Out Tracking. Same marketing
              guard as the TabsTrigger above — marketing HODs don't
              do field visits, so the panel body is also gated to
              avoid an empty pane if the URL or state ever lands
              on 'movements' under marketing scope. */}
          <TabsContent value="movements" className="mt-6" forceMount={true} hidden={activeTab !== 'movements' || selectedDepartment === 'marketing'}>
            {mountedTabs.has('movements') && selectedDepartment !== 'marketing' && (
            <ScrollArea className="h-[300px]">
              <div className="flex items-center gap-2 mb-2">
                <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  {deptLabel ? `${deptLabel} HOD In & Out` : 'HOD In & Out'}
                </h4>
                <HelpInfo
                  align="inline"
                  title="How to read the In & Out tab"
                  steps={[
                    'One row per HOD field movement — when they left the office, came back, and where they went.',
                    <>The <strong>Status</strong> column shows whether they\'re currently out or back; <strong>Check In / Check Out</strong> are the timestamps.</>,
                    'Location shows the destination they logged for the trip.',
                    'A blank Check Out with an old Check In means an HOD is still marked out — worth a nudge if it\'s been hours.',
                  ]}
                  tip="HODs should model good field-logging discipline — if their own In & Out trail is patchy, their agents’ will be too."
                />
              </div>
              <div className="space-y-3">
                {visibleHodMovements.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={`border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                          <th className={`text-left py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>HOD</th>
                          <th className={`text-center py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Department</th>
                          <th className={`text-center py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Status</th>
                          <th className={`text-center py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Check In</th>
                          <th className={`text-center py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Check Out</th>
                          <th className={`text-center py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Location</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleHodMovements.map((movement, idx) => (
                          <tr key={movement.id || idx} className={`border-b ${darkMode ? 'border-slate-800' : 'border-slate-100'}`}>
                            <td className={`py-3 px-3 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Crown size={14} className="text-amber-500" />
                                <span>{movement.hod_name}</span>
                                {/* Trip counter shows up only when the HOD
                                    had more than one site visit today, so
                                    a regular single-trip day stays clean. */}
                                {movement.trip_total > 1 && (
                                  <Badge variant="outline" className="text-xs text-slate-500 border-slate-300">
                                    Trip {movement.trip_number} of {movement.trip_total}
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-3 text-center">
                              <Badge className={movement.department === 'Sales' ? 'bg-blue-500' : 'bg-purple-500'}>
                                {movement.department}
                              </Badge>
                            </td>
                            <td className="py-3 px-3 text-center">
                              {movement.status === 'in_field' ? (
                                <Badge className="bg-emerald-500">
                                  <MapPin size={12} className="mr-1" /> In Field
                                </Badge>
                              ) : movement.status === 'in_office' ? (
                                <Badge className="bg-blue-500">
                                  <Building size={12} className="mr-1" /> In Office
                                </Badge>
                              ) : movement.status === 'report_submitted' ? (
                                <div className="flex items-center gap-1 justify-center flex-wrap">
                                  <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300">
                                    <CheckCircle2 size={12} className="mr-1" /> Report Submitted
                                  </Badge>
                                  <MovementOpenReportButton movement={movement} darkMode={darkMode} />
                                </div>
                              ) : (
                                <Badge variant="outline" className="text-amber-500 border-amber-500">
                                  <AlertTriangle size={12} className="mr-1" /> Not Updated
                                </Badge>
                              )}
                            </td>
                            <td className={`py-3 px-3 text-center ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                              {movement.check_in ? (
                                <div className="flex items-center justify-center gap-1">
                                  <LogIn size={12} className="text-emerald-500" />
                                  {new Date(movement.check_in).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </div>
                              ) : (
                                <span className="text-amber-500">--:--</span>
                              )}
                            </td>
                            <td className={`py-3 px-3 text-center ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                              {movement.check_out ? (
                                <div className="flex items-center justify-center gap-1">
                                  <LogOut size={12} className="text-red-500" />
                                  {new Date(movement.check_out).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </div>
                              ) : (
                                <span className="text-slate-400">--:--</span>
                              )}
                            </td>
                            <td className={`py-3 px-3 text-center text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              {movement.location || 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className={`text-center py-8 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    <MapPin size={32} className="mx-auto mb-2 text-slate-400" />
                    No movement data available for HODs
                  </div>
                )}
              </div>
            </ScrollArea>
            )}
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts" className="mt-6 space-y-4" forceMount={true} hidden={activeTab !== 'alerts'}>
            {mountedTabs.has('alerts') && (
            <>
            {/* Alerts & Flags — HOD-subject flags. Same panel the HOD
                dashboard uses for agents; here it's scoped to HODs so
                the CEO can send Feedback / Warning straight to an HOD. */}
            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <AlertTriangle size={16} className="text-red-500" />
                <h4 className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  {deptLabel
                    ? `Alerts & Flags — ${deptLabel} HODs`
                    : 'Alerts & Flags — HODs'}
                </h4>
                {visibleHodFlagAlerts.length > 0 && (
                  <Badge variant="destructive" className="ml-1">{visibleHodFlagAlerts.length}</Badge>
                )}
                <HelpInfo
                  align="inline"
                  title="How to use the Alerts tab"
                  steps={[
                    'Issues the system detected against your HODs — silent days, overdue follow-ups, stuck deals, OOO without a return time.',
                    'Each alert shows when it was raised and how long the condition has been true.',
                    <>Click <strong>Feedback</strong> or <strong>Warning</strong> on any alert to message that HOD directly — the composer pre-fills a tailored message you can edit before sending.</>,
                    'The badge is the live HOD-alert count; it drops as issues clear. Agent-level flags stay on each HOD\'s own dashboard.',
                  ]}
                  tip="Use Warning sparingly — for repeat issues. A first slip is usually better handled with Feedback."
                />
              </div>
              <AlertsFlagsPanel alerts={visibleHodFlagAlerts} loading={loading} token={token} />
            </div>
            </>
            )}
          </TabsContent>

          {/* CEO Comments Tab — recipient-style card feed that
              mirrors the layout of the HOD's "Comments from CEO" tab.
              The CEO sees their sent comments rendered as the cards
              the HODs see, with a Reply button on each so they can
              add follow-ups inline. The composer view (CEOCommentsSection)
              is still available on the page body below the embed
              for starting new threads. */}
          <TabsContent value="comments" className="mt-6" forceMount={true} hidden={activeTab !== 'comments'}>
            {mountedTabs.has('comments') && (
              <CEOSentCommentsFeed
                comments={comments}
                token={token}
                onSendComment={onSendComment}
                darkMode={darkMode}
              />
            )}
          </TabsContent>
          </Tabs>
        </div>
      </CardContent>
      
      {/* HOD Report Detail Modal - Same format as HOD Dashboard */}
      <Dialog open={reportModalOpen} onOpenChange={setReportModalOpen}>
        <DialogContent className={`max-w-2xl max-h-[90vh] overflow-hidden p-0 ${darkMode ? 'bg-[#1e1e2f] border-slate-700' : 'bg-white'}`}>
          {/* Header — DialogContent renders its own close button in the
              top-right corner, so we don't add a second one here. The
              title reflects the report's source: HOD daily / Agent daily /
              Site visit, plus the person's name. */}
          <div className={`flex items-center px-6 py-4 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <FileText size={18} className={darkMode ? 'text-slate-400' : 'text-slate-600'} />
              <span className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {(() => {
                  const t = selectedReport?.type;
                  const hodAuthor = !!selectedReport?.is_hod_author
                    || (selectedReport?.sender_role || '').toLowerCase().startsWith('hod');
                  if (t === 'site_visit_report') return hodAuthor ? 'Site Visit Report · HOD' : 'Site Visit Report';
                  if (t === 'agent_daily_report') return 'Daily Report - Agent';
                  return 'Daily Report - HOD';
                })()}
              </span>
              {selectedReport?.hod_name && (
                <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  · {selectedReport.hod_name}
                </span>
              )}
              {selectedReport?.department && (
                <Badge className={
                  selectedReport.department === 'Sales' ? 'bg-blue-500'
                    : selectedReport.department === 'Marketing' ? 'bg-purple-500'
                    : 'bg-slate-500'
                }>
                  {selectedReport.department}
                </Badge>
              )}
            </div>
          </div>
          
          {selectedReport && (
            <div className="flex flex-col h-full">
              {/* Generated timestamp and Mark as Read */}
              <div className={`flex items-center justify-between px-6 py-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                <span className="text-sm">
                  Generated: {selectedReport.submitted_at ? new Date(selectedReport.submitted_at).toLocaleString() : 'N/A'}
                </span>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className={`text-xs ${darkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : ''}`}
                  >
                    <Eye size={14} className="mr-1" />
                    Mark as Read
                  </Button>
                  <Badge variant="outline" className="text-xs">
                    v{selectedReport.version || 1}
                  </Badge>
                </div>
              </div>
              
              {/* Report Content - Scrollable */}
              <ScrollArea className="flex-1 px-6" style={{ maxHeight: '300px' }}>
                <div className="py-4 space-y-6">
                  {selectedReport.full_report ? (
                    <>
                      {/* Parse and render sections */}
                      {(() => {
                        const sections = [];
                        let currentSection = null;
                        let currentContent = [];
                        
                        selectedReport.full_report.split('\n').forEach((line, idx) => {
                          if (line.startsWith('## ')) {
                            if (currentSection) {
                              sections.push({ title: currentSection, content: [...currentContent] });
                            }
                            currentSection = line.replace('## ', '');
                            currentContent = [];
                          } else if (line.trim()) {
                            currentContent.push(line);
                          }
                        });
                        if (currentSection) {
                          sections.push({ title: currentSection, content: [...currentContent] });
                        }
                        
                        // If no sections found, treat whole text as one section
                        if (sections.length === 0 && selectedReport.full_report.trim()) {
                          sections.push({ 
                            title: 'Daily Summary', 
                            content: selectedReport.full_report.split('\n').filter(l => l.trim()) 
                          });
                        }
                        
                        return sections.map((section, sIdx) => (
                          <div key={sIdx} className="space-y-2">
                            <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                              {section.title}
                            </h3>
                            {section.content.map((line, lIdx) => {
                              if (line.startsWith('- ') || line.startsWith('• ')) {
                                return (
                                  <p key={lIdx} className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                    • {line.replace(/^[-•]\s*/, '')}
                                  </p>
                                );
                              }
                              return (
                                <p key={lIdx} className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                                  {line}
                                </p>
                              );
                            })}
                          </div>
                        ));
                      })()}
                    </>
                  ) : (
                    <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      No detailed report content available.
                    </p>
                  )}
                </div>
              </ScrollArea>
              
              {/* Private Note Section */}
              <div className={`px-6 py-4 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <p className={`text-sm font-medium mb-2 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Add Private Note <span className={`font-normal ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>(not visible to HOD)</span>
                </p>
                <div className="flex gap-2">
                  <Input 
                    placeholder="Add a note about this report..."
                    className={`flex-1 ${darkMode ? 'bg-slate-800 border-slate-600 text-white placeholder:text-slate-500' : ''}`}
                  />
                  <Button size="icon" className="bg-violet-600 hover:bg-violet-700">
                    <Plus size={18} />
                  </Button>
                </div>
              </div>
              
              {/* Send Feedback Section — wires to the live endpoints:
                  HOD-authored reports go via /api/ceo/comments to the right
                  target_hod; agent-authored reports go via
                  /api/hod/agents/{id}/direct-message (which already mirrors
                  into report_feedback so the agent sees it in their
                  Feedback from Management card, and also notifies their HOD
                  via the existing fan-out). */}
              {(() => {
                const hodAuthor = !!selectedReport?.is_hod_author
                  || (selectedReport?.sender_role || '').toLowerCase().startsWith('hod');
                const isAgentReport = selectedReport?.type === 'agent_daily_report'
                  || (selectedReport?.type === 'site_visit_report' && !hodAuthor);
                const label = hodAuthor ? 'Send Feedback to HOD' : 'Send Feedback to Agent';
                const visibility = isAgentReport ? '(visible to HOD and Agent)' : '(visible to HOD)';
                const placeholder = hodAuthor ? 'Write feedback for the HOD…' : 'Write feedback for the Agent…';

                const sendReportFeedback = async () => {
                  const body = (reportFeedbackText || '').trim();
                  if (!body) { toast.error('Write a message before sending'); return; }
                  setReportFeedbackSending(true);
                  try {
                    if (hodAuthor) {
                      const dept = (selectedReport?.department || '').toLowerCase();
                      const targetHod = dept === 'marketing' ? 'hod_marketing'
                                      : dept === 'sales' ? 'hod_sales' : 'all';
                      const res = await fetch(`${API_URL}/api/ceo/comments`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          comment_text: body,
                          target_hod: targetHod,
                          priority: 'normal',
                          linked_to: {
                            recordType: 'report',
                            recordId: selectedReport?.id,
                            recordLabel: `${selectedReport?.type || 'report'} · ${selectedReport?.hod_name || ''}`,
                          },
                        }),
                      });
                      const data = await res.json();
                      if (!data.success) { toast.error(data.error || data.detail || 'Failed to send'); return; }
                      toast.success('Feedback sent to HOD');
                    } else if (selectedReport?.hod_id) {
                      const res = await fetch(`${API_URL}/api/hod/agents/${selectedReport.hod_id}/direct-message`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ feedback_text: body, agent_id: selectedReport.hod_id }),
                      });
                      const data = await res.json();
                      if (!data.success) { toast.error(data.detail || 'Failed to send'); return; }
                      toast.success(`Feedback sent to ${selectedReport.hod_name || 'agent'}`);
                    } else {
                      toast.error('No recipient on this report');
                      return;
                    }
                    setReportFeedbackText('');
                    // Close the report dialog once the feedback is on its way.
                    setReportModalOpen(false);
                  } catch (e) {
                    toast.error(`Send failed: ${e.message || e}`);
                  } finally {
                    setReportFeedbackSending(false);
                  }
                };

                return (
                  <div className={`px-6 py-4 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <p className="text-sm font-medium mb-2 flex items-center gap-1 flex-wrap">
                      <MessageSquare size={14} className="text-emerald-500" />
                      <span className="text-emerald-500">{label}</span>
                      <span className={`font-normal ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{visibility}</span>
                    </p>
                    <div className="flex gap-2">
                      <Input
                        placeholder={placeholder}
                        value={reportFeedbackText}
                        onChange={(e) => setReportFeedbackText(e.target.value)}
                        disabled={reportFeedbackSending}
                        className={`flex-1 ${darkMode ? 'bg-slate-800 border-slate-600 text-white placeholder:text-slate-500' : ''}`}
                      />
                      <Button
                        size="icon"
                        className="bg-violet-600 hover:bg-violet-700"
                        disabled={reportFeedbackSending || !reportFeedbackText.trim()}
                        onClick={sendReportFeedback}
                      >
                        {reportFeedbackSending ? <RefreshCw size={18} className="animate-spin" /> : <Send size={18} />}
                      </Button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Task Detail Modal */}
      <Dialog open={taskModalOpen} onOpenChange={setTaskModalOpen}>
        <DialogContent className={`max-w-md p-0 overflow-hidden ${darkMode ? 'bg-[#1e1e2f] border-slate-700' : 'bg-white'}`}>
          {/* Header — DialogContent provides its own × button in the
              top-right corner, so we don't add a second one. */}
          <div className={`flex items-center px-5 py-4 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <div className="flex items-center gap-2">
              <Activity className="text-violet-500" size={18} />
              <span className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>Task Details</span>
            </div>
          </div>
          
          {selectedTask && (
            <div className="px-5 py-4 space-y-5">
              {/* Task Title and Description */}
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-slate-800/50' : 'bg-slate-100'}`}>
                <h3 className={`font-semibold text-base ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  {selectedTask.title}
                </h3>
                {selectedTask.description && (
                  <p className={`text-sm mt-2 ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    {selectedTask.description}
                  </p>
                )}
              </div>
              
              {/* Info Grid - Using table-like structure for better alignment */}
              <div className="space-y-4">
                {/* Row 1: Assigned By / Assigned To */}
                <div className="flex">
                  <div className="flex-1">
                    <p className={`text-xs mb-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Assigned By</p>
                    <p className={`text-sm font-medium ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                      {selectedTask.assigned_by_name || 'CEO'}
                    </p>
                  </div>
                  <div className="flex-1">
                    <p className={`text-xs mb-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Assigned To</p>
                    <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {selectedTask.assigned_to_name || 'Unassigned'}
                    </p>
                  </div>
                </div>
                
                {/* Row 2: Status / Priority */}
                <div className="flex">
                  <div className="flex-1">
                    <p className={`text-xs mb-1.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Status</p>
                    <Badge variant={selectedTask.status === 'completed' ? 'default' : selectedTask.status === 'in_progress' ? 'secondary' : 'outline'}
                           className={selectedTask.status === 'completed' ? 'bg-emerald-500' : ''}>
                      {selectedTask.status?.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <div className="flex-1">
                    <p className={`text-xs mb-1.5 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Priority</p>
                    <Badge variant={selectedTask.priority === 'urgent' ? 'destructive' : selectedTask.priority === 'high' ? 'secondary' : 'outline'}>
                      {selectedTask.priority}
                    </Badge>
                  </div>
                </div>
                
                {/* Row 3: Created */}
                <div>
                  <p className={`text-xs mb-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Created</p>
                  <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    {selectedTask.created_at ? new Date(selectedTask.created_at).toLocaleString() : 'N/A'}
                  </p>
                </div>
                
                {/* Row 4: Due Date (if exists) */}
                {selectedTask.due_date && (
                  <div>
                    <p className={`text-xs mb-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Due Date</p>
                    <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {new Date(selectedTask.due_date).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Close Button */}
              <div className={`flex justify-end pt-3 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <Button variant="outline" size="sm" onClick={() => setTaskModalOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* New Task Modal — DialogContent ships its own close button in
          the top-right corner, so we don't render a second one here. */}
      <Dialog open={newTaskModalOpen} onOpenChange={setNewTaskModalOpen}>
        <DialogContent className={`max-w-md p-0 overflow-hidden ${darkMode ? 'bg-[#1e1e2f] border-slate-700' : 'bg-white'}`}>
          {/* Header */}
          <div className={`flex items-center px-5 py-4 border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <div className="flex items-center gap-2">
              <Plus className="text-violet-500" size={18} />
              <span className={`font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>Assign New Task</span>
            </div>
          </div>
          
          <div className="px-5 py-4 space-y-4">
            {/* Task Title */}
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                Task Title <span className="text-red-500">*</span>
              </label>
              <Input 
                placeholder="Enter task title..."
                value={newTask.title}
                onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                className={darkMode ? 'bg-slate-800 border-slate-600 text-white' : ''}
              />
            </div>
            
            {/* Task Description */}
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                Description
              </label>
              <Textarea 
                placeholder="Enter task description..."
                value={newTask.description}
                onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                className={darkMode ? 'bg-slate-800 border-slate-600 text-white' : ''}
                rows={3}
              />
            </div>
            
            {/* Assign To Type */}
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                Assign To <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={newTask.assigned_to_type === 'hod' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setNewTask({...newTask, assigned_to_type: 'hod', assigned_to: ''})}
                  className={newTask.assigned_to_type === 'hod' ? 'bg-violet-600' : ''}
                >
                  <Crown size={14} className="mr-1" /> HOD
                </Button>
                <Button
                  type="button"
                  variant={newTask.assigned_to_type === 'agent' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setNewTask({...newTask, assigned_to_type: 'agent', assigned_to: ''})}
                  className={newTask.assigned_to_type === 'agent' ? 'bg-violet-600' : ''}
                >
                  <Users size={14} className="mr-1" /> Agent
                </Button>
              </div>
            </div>
            
            {/* Select User */}
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                Select {newTask.assigned_to_type === 'hod' ? 'HOD' : 'Agent'} <span className="text-red-500">*</span>
              </label>
              <select
                value={newTask.assigned_to}
                onChange={(e) => setNewTask({...newTask, assigned_to: e.target.value})}
                className={`w-full rounded-md border p-2 text-sm ${
                  darkMode 
                    ? 'bg-slate-800 border-slate-600 text-white' 
                    : 'bg-white border-slate-300 text-slate-900'
                }`}
              >
                <option value="">Select {newTask.assigned_to_type === 'hod' ? 'HOD' : 'Agent'}...</option>
                {availableUsers
                  .filter((u) => {
                    const role = (u.role || '').toLowerCase();
                    if (newTask.assigned_to_type === 'hod') {
                      return role.startsWith('hod');
                    }
                    return role.includes('agent') || role.includes('sales') || role.includes('marketing') || role === 'cr_agent';
                  })
                  .map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.full_name || user.email} ({user.role})
                    </option>
                  ))}
              </select>
            </div>
            
            {/* Priority */}
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                Priority
              </label>
              <div className="flex gap-2">
                {['normal', 'high', 'urgent'].map(p => (
                  <Button
                    key={p}
                    type="button"
                    variant={newTask.priority === p ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setNewTask({...newTask, priority: p})}
                    className={newTask.priority === p ? (p === 'urgent' ? 'bg-red-600' : p === 'high' ? 'bg-amber-600' : 'bg-violet-600') : ''}
                  >
                    {p.charAt(0).toUpperCase() + p.slice(1)}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Due Date */}
            <div>
              <label className={`block text-sm font-medium mb-1.5 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                Due Date (Optional)
              </label>
              <Input 
                type="date"
                value={newTask.due_date}
                onChange={(e) => setNewTask({...newTask, due_date: e.target.value})}
                className={darkMode ? 'bg-slate-800 border-slate-600 text-white' : ''}
              />
            </div>
          </div>
            
          {/* Actions */}
          <div className={`flex justify-end gap-2 px-5 py-4 border-t ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
            <Button variant="outline" size="sm" onClick={() => setNewTaskModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateTask}
              size="sm"
              className="bg-violet-600 hover:bg-violet-700"
            >
              <Send size={14} className="mr-1" />
              Assign Task
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

// ============ AI Daily Brief Section ============
const AIDailyBrief = ({ brief, onRefresh, refreshing, token }) => {
  const { isDarkMode } = useTheme();
  const [openSections, setOpenSections] = useState({
    executive: true,
    sales: true,
    team: true,
    marketing: true,
    forecast: true,
    flags: true,
    yesterday: false
  });
  const [selectedDate, setSelectedDate] = useState('');
  const [historicalBrief, setHistoricalBrief] = useState(null);
  const [loadingDate, setLoadingDate] = useState(false);
  const [sharing, setSharing] = useState(false);
  // Share-to-HODs dialog: lets the CEO attach a note alongside the brief.
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareNote, setShareNote] = useState('');

  const onDateChange = async (date) => {
    setSelectedDate(date);
    if (!date) { setHistoricalBrief(null); return; }
    setLoadingDate(true);
    try {
      const res = await fetch(`${API_URL}/api/ceo/brief?date=${date}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setHistoricalBrief(data?.data || null);
      if (!data?.data) toast(`No brief was generated on ${date}`);
    } catch (e) {
      toast.error(`Failed to load brief: ${e.message || e}`);
    } finally {
      setLoadingDate(false);
    }
  };
  
  // Dark mode detection
  const [darkMode, setDarkMode] = useState(isDarkMode);
  useEffect(() => {
    const checkDark = () => setDarkMode(document.documentElement.classList.contains('dark'));
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [isDarkMode]);
  
  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  // The visible brief is the historical one if a date was picked, else live.
  const activeBrief = historicalBrief || brief;
  if (!activeBrief && !loadingDate) {
    return (
      <Card className={darkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}>
        <CardContent className="p-8 text-center">
          <Sparkles className={`mx-auto mb-4 ${darkMode ? 'text-violet-400' : 'text-violet-500'}`} size={48} />
          <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            No Brief Generated Yet
          </h3>
          <p className={`text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Click below to generate your AI executive brief
          </p>
          <Button onClick={onRefresh} disabled={refreshing}>
            {refreshing ? <RefreshCw size={16} className="mr-2 animate-spin" /> : <Sparkles size={16} className="mr-2" />}
            Generate Brief
          </Button>
        </CardContent>
      </Card>
    );
  }

  const briefData = (activeBrief && (activeBrief.brief_text || activeBrief.brief)) || {};

  // Render the brief as plain Markdown — used for both download and share.
  const buildMarkdown = () => {
    const lines = [];
    lines.push(`# AI Executive Brief — ${activeBrief?.date || ''} (v${activeBrief?.version || ''})`);
    lines.push(`Generated at: ${activeBrief?.generated_at || ''}`);
    lines.push('');
    if (briefData.executive_summary) {
      lines.push('## Executive Summary'); lines.push(briefData.executive_summary); lines.push('');
    }
    const sp = briefData.sales_performance || {};
    if (Object.keys(sp).length) {
      lines.push('## Sales Performance');
      Object.entries(sp).forEach(([k, v]) => lines.push(`- **${k}**: ${typeof v === 'string' ? v : JSON.stringify(v)}`));
      lines.push('');
    }
    const tp = briefData.team_pulse || {};
    if (Object.keys(tp).length) {
      lines.push('## Sales Team Pulse');
      Object.entries(tp).forEach(([k, v]) => lines.push(`- **${k}**: ${typeof v === 'string' ? v : JSON.stringify(v)}`));
      lines.push('');
    }
    const mu = briefData.marketing_update || briefData.marketing || {};
    if (Object.keys(mu).length) {
      lines.push('## Marketing Update');
      Object.entries(mu).forEach(([k, v]) => lines.push(`- **${k}**: ${typeof v === 'string' ? v : JSON.stringify(v)}`));
      lines.push('');
    }
    const fc = briefData.forecast || {};
    if (Object.keys(fc).length) {
      lines.push('## Forecast'); Object.entries(fc).forEach(([k, v]) => lines.push(`- **${k}**: ${typeof v === 'string' ? v : JSON.stringify(v)}`));
      lines.push('');
    }
    if (Array.isArray(briefData.flags_and_attention) && briefData.flags_and_attention.length) {
      lines.push('## Flags & Attention');
      briefData.flags_and_attention.forEach((f) => lines.push(`- ${typeof f === 'string' ? f : JSON.stringify(f)}`));
      lines.push('');
    }
    if (Array.isArray(briefData.next_actions) && briefData.next_actions.length) {
      lines.push('## Next Actions');
      briefData.next_actions.forEach((a) => lines.push(`- ${typeof a === 'string' ? a : JSON.stringify(a)}`));
      lines.push('');
    }
    return lines.join('\n');
  };

  const downloadMarkdown = () => {
    const md = buildMarkdown();
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `executive-brief-${activeBrief?.date || 'today'}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const shareToHods = async () => {
    if (sharing) return;
    setSharing(true);
    try {
      const md = buildMarkdown();
      const res = await fetch(`${API_URL}/api/ceo/brief/share-to-hods`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brief_date: activeBrief?.date,
          markdown: md,
          note: shareNote.trim() || null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Shared with ${data.data?.recipient_count || 0} HOD${data.data?.recipient_count === 1 ? '' : 's'}`);
        setShareDialogOpen(false);
        setShareNote('');
      } else {
        toast.error(data.detail || 'Failed to share');
      }
    } catch (e) {
      toast.error(`Share failed: ${e.message || e}`);
    } finally {
      setSharing(false);
    }
  };
  const hasFlags = briefData?.flags_and_attention?.length > 0;
  
  const SectionHeader = ({ title, icon: Icon, section, hasAlert }) => (
    <CollapsibleTrigger 
      className={`flex items-center justify-between w-full p-3 rounded-lg ${
        darkMode ? 'hover:bg-slate-700/50' : 'hover:bg-slate-100'
      }`}
      onClick={() => toggleSection(section)}
    >
      <div className="flex items-center gap-2">
        <Icon size={18} className={hasAlert ? 'text-red-500' : darkMode ? 'text-violet-400' : 'text-violet-500'} />
        <span className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>{title}</span>
        {hasAlert && <Badge variant="destructive" className="text-xs">Attention</Badge>}
      </div>
      {openSections[section] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
    </CollapsibleTrigger>
  );
  
  return (
    <Card className={darkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle className={`flex items-center gap-2 flex-wrap ${darkMode ? 'text-white' : ''}`}>
              <Sparkles className="text-violet-500" size={20} />
              AI Executive Brief
              {historicalBrief && (
                <Badge variant="outline" className="ml-2 text-amber-700 border-amber-300 bg-amber-50">
                  Historical
                </Badge>
              )}
              <HelpInfo
                align="inline"
                title="How to use the AI Executive Brief"
                steps={[
                  'The brief is generated once per day from real operational data — sales, marketing, team activity, alerts — and structured into executive-friendly sections.',
                  <>Sections include: <em>Executive Summary</em>, <em>Sales Performance</em>, <em>Sales Team Pulse</em>, <em>Marketing Update</em>, <em>Pipeline Forecast</em>, <em>Flags & Attention</em>, <em>Yesterday Recap</em>.</>,
                  'Use the date picker to load a historical brief — useful for end-of-week reviews.',
                  'Click Share to HODs to forward the brief with an optional note; HODs see it in their CEO Comments tab.',
                  'Click Download (Markdown) to save the full brief for board prep or out-of-app sharing.',
                  'Click Generate to recompute the brief mid-day after major events (closed mega-deal, market news, etc.).',
                ]}
                tip="Read the Flags & Attention section first — that's where the next 24 hours of decisions usually hide."
              />
            </CardTitle>
            <CardDescription className={darkMode ? 'text-slate-400' : ''}>
              {activeBrief?.date} • v{activeBrief?.version} • Updated {activeBrief?.generated_at ? new Date(activeBrief.generated_at).toLocaleTimeString() : '—'}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => onDateChange(e.target.value)}
              className={`h-8 text-xs rounded border px-2 ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'border-slate-300'}`}
              title="Pick a date to view a historical brief"
            />
            {selectedDate && (
              <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => onDateChange('')}>
                Today
              </Button>
            )}
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setShareDialogOpen(true)}>
              <Send size={12} className="mr-1" />
              Share to HODs
            </Button>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={downloadMarkdown}>
              <FileText size={12} className="mr-1" /> Download
            </Button>
            <Button variant="outline" size="sm" className="h-8" onClick={onRefresh} disabled={refreshing} title="Re-generate today's brief">
              {refreshing ? <RefreshCw size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {/* Executive Summary */}
        <Collapsible open={openSections.executive}>
          <SectionHeader title="Executive Summary" icon={FileText} section="executive" />
          <CollapsibleContent>
            <div className={`p-4 rounded-lg ${darkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
              <p className={darkMode ? 'text-slate-300' : 'text-slate-700'}>
                {briefData?.executive_summary}
              </p>
            </div>
          </CollapsibleContent>
        </Collapsible>
        
        {/* Sales Performance */}
        <Collapsible open={openSections.sales}>
          <SectionHeader title="Sales Performance" icon={DollarSign} section="sales" />
          <CollapsibleContent>
            <div className={`p-4 rounded-lg space-y-3 ${darkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
              <div className="flex items-center gap-2">
                <Badge variant={briefData?.sales_performance?.pipeline_health === 'healthy' ? 'success' : 'warning'}>
                  Pipeline: {briefData?.sales_performance?.pipeline_health}
                </Badge>
              </div>
              <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                <strong>Deals:</strong> {briefData?.sales_performance?.deals_closed_today}
              </p>
              <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                <strong>Top Performer:</strong> {briefData?.sales_performance?.top_performer}
              </p>
              <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                <strong>Conversion:</strong> {briefData?.sales_performance?.conversion_rate_vs_last_week}
              </p>
              {briefData?.sales_performance?.zero_activity_agents?.length > 0 && (
                <div className={`p-2 rounded ${darkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
                  <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                    <AlertTriangle size={14} className="inline mr-1" />
                    <strong>Zero Activity:</strong> {briefData.sales_performance.zero_activity_agents.join(', ')}
                  </p>
                </div>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
        
        {/* Sales Team Pulse */}
        <Collapsible open={openSections.team}>
          <SectionHeader title="Sales Team Pulse" icon={Users} section="team" />
          <CollapsibleContent>
            <div className={`p-4 rounded-lg space-y-3 ${darkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
              <Badge variant={
                briefData?.sales_team_pulse?.activity_level === 'High' ? 'success' :
                briefData?.sales_team_pulse?.activity_level === 'Medium' ? 'warning' : 'destructive'
              }>
                Activity: {briefData?.sales_team_pulse?.activity_level}
              </Badge>
              <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                {briefData?.sales_team_pulse?.notable_patterns}
              </p>
              <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                <strong>Competition:</strong> {briefData?.sales_team_pulse?.competition_standings}
              </p>
            </div>
          </CollapsibleContent>
        </Collapsible>
        
        {/* Marketing Update */}
        <Collapsible open={openSections.marketing}>
          <SectionHeader title="Marketing Update" icon={Megaphone} section="marketing" />
          <CollapsibleContent>
            <div className={`p-4 rounded-lg space-y-3 ${darkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
              <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                <strong>Today:</strong> {briefData?.marketing_update?.work_today}
              </p>
              <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                <strong>Campaigns:</strong> {briefData?.marketing_update?.campaigns_status}
              </p>
              {briefData?.marketing_update?.blockers && (
                <p className={`text-sm ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                  <AlertTriangle size={14} className="inline mr-1" />
                  <strong>Blockers:</strong> {briefData.marketing_update.blockers}
                </p>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
        
        {/* Pipeline Forecast */}
        <Collapsible open={openSections.forecast}>
          <SectionHeader title="Pipeline Forecast" icon={TrendingUp} section="forecast" />
          <CollapsibleContent>
            <div className={`p-4 rounded-lg space-y-3 ${darkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
              <Badge variant={
                briefData?.pipeline_forecast?.confidence === 'High' ? 'success' :
                briefData?.pipeline_forecast?.confidence === 'Medium' ? 'warning' : 'destructive'
              }>
                Confidence: {briefData?.pipeline_forecast?.confidence}
              </Badge>
              <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                {briefData?.pipeline_forecast?.likely_to_close_this_week}
              </p>
              <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                <strong>Revenue Estimate:</strong> {briefData?.pipeline_forecast?.estimated_revenue}
              </p>
            </div>
          </CollapsibleContent>
        </Collapsible>
        
        {/* Flags & Attention */}
        <Collapsible open={openSections.flags}>
          <SectionHeader title="Flags & Attention Items" icon={AlertTriangle} section="flags" hasAlert={hasFlags} />
          <CollapsibleContent>
            <div className={`p-4 rounded-lg ${hasFlags ? (darkMode ? 'bg-red-900/20 border border-red-700/50' : 'bg-red-50 border border-red-200') : (darkMode ? 'bg-slate-800/50' : 'bg-slate-50')}`}>
              {hasFlags ? (
                <ul className="space-y-2">
                  {briefData.flags_and_attention.map((flag, idx) => (
                    <li key={idx} className={`flex items-start gap-2 text-sm ${darkMode ? 'text-red-300' : 'text-red-700'}`}>
                      <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                      {flag}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={`text-sm flex items-center gap-2 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                  <CheckCircle2 size={14} /> All clear - no items requiring attention
                </p>
              )}
            </div>
          </CollapsibleContent>
        </Collapsible>
        
        {/* Next Actions — explicit CEO TODO list pulled from the AI prompt */}
        {Array.isArray(briefData?.next_actions) && briefData.next_actions.length > 0 && (
          <Collapsible open={openSections.next || true}>
            <SectionHeader title="Next Actions" icon={CheckCircle2} section="next" />
            <CollapsibleContent>
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-emerald-900/20 border border-emerald-700/50' : 'bg-emerald-50 border border-emerald-200'}`}>
                <ol className="space-y-2 list-decimal list-inside">
                  {briefData.next_actions.map((action, idx) => (
                    <li key={idx} className={`text-sm ${darkMode ? 'text-emerald-200' : 'text-emerald-800'}`}>
                      {typeof action === 'string' ? action : JSON.stringify(action)}
                    </li>
                  ))}
                </ol>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Yesterday Recap */}
        <Collapsible open={openSections.yesterday}>
          <SectionHeader title="Yesterday Recap" icon={Calendar} section="yesterday" />
          <CollapsibleContent>
            <div className={`p-4 rounded-lg space-y-2 ${darkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
              <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                {briefData?.yesterday_recap?.summary}
              </p>
              <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                <strong>vs Today:</strong> {briefData?.yesterday_recap?.comparison}
              </p>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>

      {/* Share-to-HODs dialog: optional message + brief preview, then send. */}
      {shareDialogOpen && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999,
            backgroundColor: 'rgba(15, 23, 42, 0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={(e) => { if (e.target === e.currentTarget && !sharing) setShareDialogOpen(false); }}
        >
          <div className={`w-full max-w-lg mx-4 rounded-2xl shadow-2xl border p-6 ${darkMode ? 'bg-[#1a1a2e] border-slate-700' : 'bg-white border-slate-200'}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                  Share Brief with HODs
                </h2>
                <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Brief: {activeBrief?.date} · v{activeBrief?.version}. The full markdown will be attached.
                </p>
              </div>
              <button
                onClick={() => !sharing && setShareDialogOpen(false)}
                className={`text-xl leading-none ${darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-700'}`}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <label className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
              Add a personal note (optional)
            </label>
            <Textarea
              value={shareNote}
              onChange={(e) => setShareNote(e.target.value)}
              rows={4}
              placeholder="e.g. Heads up — please review the Marketing section before our 11am call."
              className="mt-1"
              disabled={sharing}
            />
            <p className={`text-xs mt-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              The note appears at the top of the message; the brief follows beneath it.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" size="sm" onClick={() => setShareDialogOpen(false)} disabled={sharing}>
                Cancel
              </Button>
              <Button size="sm" onClick={shareToHods} disabled={sharing}>
                {sharing ? <RefreshCw size={14} className="mr-1 animate-spin" /> : <Send size={14} className="mr-1" />}
                Send to all HODs
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

// The inline Sales/Marketing Snapshot panels were extracted to
// components/SnapshotPanels.jsx so both the CEO and HOD dashboards render
// from a single source. Aliased imports at the top of this file expose
// them as `SharedSalesSnapshotPanel` / `SharedMarketingSnapshotPanel`.
const SalesSnapshotPanel = SharedSalesSnapshotPanel;
const MarketingSnapshotPanel = SharedMarketingSnapshotPanel;

// ============ Sales Team Overview Table ============
const SalesTeamOverview = ({ team, onViewReport }) => {
  const [darkMode, setDarkMode] = useState(false);
  useEffect(() => {
    const checkDark = () => setDarkMode(document.documentElement.classList.contains('dark'));
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'Active': return <Badge variant="success" className="text-xs">Active</Badge>;
      case 'Idle': return <Badge variant="warning" className="text-xs">Idle</Badge>;
      default: return <Badge variant="destructive" className="text-xs">No Activity</Badge>;
    }
  };
  
  return (
    <Card className={darkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}>
      <CardHeader className="pb-3">
        <CardTitle className={`text-base flex items-center gap-2 flex-wrap ${darkMode ? 'text-white' : ''}`}>
          <Users size={18} className="text-blue-500" />
          Sales Team Overview
          <HelpInfo
            align="inline"
            title="How to read Sales Team Overview"
            steps={[
              'One row per sales agent — sortable by leads, deals closed, conversion rate, and last activity.',
              'The Status column shows whether they\'re actively working today (Active) or have gone silent (No Activity).',
              'Click the eye icon on any row to read that agent\'s latest AI daily report — what they did, what\'s in their pipeline, and where they\'re stuck.',
              'From the report dialog, you can send Feedback or Warning directly to the agent and/or their HOD.',
            ]}
            tip="Sort by Conv. Rate ascending to find agents who need coaching; sort by Deals Closed descending to find your top performers."
          />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={`border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <th className={`text-left py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>#</th>
                <th className={`text-left py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Agent</th>
                <th className={`text-center py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Deals (Week)</th>
                <th className={`text-center py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Pipeline</th>
                <th className={`text-center py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Leads Today</th>
                <th className={`text-center py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Status</th>
                <th className={`text-center py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Action</th>
              </tr>
            </thead>
            <tbody>
              {team?.length > 0 ? team.map((agent) => (
                <tr 
                  key={agent.agent_id}
                  className={`border-b ${darkMode ? 'border-slate-800 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50'}`}
                >
                  <td className={`py-3 px-3 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                    {agent.rank}
                  </td>
                  <td className={`py-3 px-3 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                    {agent.agent_name}
                  </td>
                  <td className={`py-3 px-3 text-center font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                    {agent.deals_closed_week}
                  </td>
                  <td className={`py-3 px-3 text-center ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {agent.active_pipeline_deals}
                  </td>
                  <td className={`py-3 px-3 text-center ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {agent.leads_contacted_today}
                  </td>
                  <td className="py-3 px-3 text-center">
                    {getStatusBadge(agent.status)}
                  </td>
                  <td className="py-3 px-3 text-center">
                    <Button variant="ghost" size="sm" onClick={() => onViewReport(agent)}>
                      <Eye size={14} />
                    </Button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={7} className={`py-8 text-center ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    No sales team members found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// ============ Marketing Team Overview Table ============
const MarketingTeamOverview = ({ team, onViewReport }) => {
  const [darkMode, setDarkMode] = useState(false);
  useEffect(() => {
    const checkDark = () => setDarkMode(document.documentElement.classList.contains('dark'));
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'Active': return <Badge variant="success" className="text-xs">Active</Badge>;
      case 'Idle': return <Badge variant="warning" className="text-xs">Idle</Badge>;
      default: return <Badge variant="destructive" className="text-xs">No Activity</Badge>;
    }
  };
  
  return (
    <Card className={darkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}>
      <CardHeader className="pb-3">
        <CardTitle className={`text-base flex items-center gap-2 flex-wrap ${darkMode ? 'text-white' : ''}`}>
          <Users size={18} className="text-purple-500" />
          Marketing Team Overview
          <HelpInfo
            align="inline"
            title="How to read Marketing Team Overview"
            steps={[
              'One row per marketing team member — sortable by campaigns, leads generated, activities, and last active.',
              'The Status column shows whether they\'re actively working today.',
              'Click the eye icon on any row to read that agent\'s AI daily report — what campaigns ran, lead generation numbers, and any flags.',
              'Send Feedback or Warning from inside the report dialog — same flow as Sales.',
            ]}
            tip="A high Leads Gen with low Campaigns Active usually means a single hit campaign — find out what worked and clone it."
          />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className={`border-b ${darkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                <th className={`text-left py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>#</th>
                <th className={`text-left py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Agent</th>
                <th className={`text-center py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Campaigns</th>
                <th className={`text-center py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Leads Gen</th>
                <th className={`text-center py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Activities</th>
                <th className={`text-center py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Status</th>
                <th className={`text-center py-2 px-3 font-medium ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Action</th>
              </tr>
            </thead>
            <tbody>
              {team?.length > 0 ? team.map((agent, idx) => (
                <tr 
                  key={agent.agent_id || idx}
                  className={`border-b ${darkMode ? 'border-slate-800 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50'}`}
                >
                  <td className={`py-3 px-3 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                    {agent.rank || idx + 1}
                  </td>
                  <td className={`py-3 px-3 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                    {agent.agent_name}
                  </td>
                  <td className={`py-3 px-3 text-center font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                    {agent.campaigns_managed || 0}
                  </td>
                  <td className={`py-3 px-3 text-center ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {agent.leads_generated || agent.leads_contacted_today || 0}
                  </td>
                  <td className={`py-3 px-3 text-center ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {agent.activities_today || 0}
                  </td>
                  <td className="py-3 px-3 text-center">
                    {getStatusBadge(agent.status)}
                  </td>
                  <td className="py-3 px-3 text-center">
                    <Button variant="ghost" size="sm" onClick={() => onViewReport(agent)}>
                      <Eye size={14} />
                    </Button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={7} className={`py-8 text-center ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    No marketing team members found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// ============ Competition Leaderboard Preview ============
const CompetitionPreview = ({ data }) => {
  const [darkMode, setDarkMode] = useState(false);
  useEffect(() => {
    const checkDark = () => setDarkMode(document.documentElement.classList.contains('dark'));
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  if (!data) return null;

  // Detect whether we have anything worth rendering. If the leaderboard
  // is empty AND no per-competition leader was found, the panel would
  // otherwise show a header + a totally blank body — confusing. We
  // surface an explanatory empty state instead so the user knows the
  // panel is working and just has no data yet.
  const hasTop3 = Array.isArray(data.top_3) && data.top_3.length > 0;
  const hasAnyLeader = !!(
    data.competition_leaders?.deal_closer
    || data.competition_leaders?.lead_converter
    || data.competition_leaders?.ai_challenge
  );
  const isEmpty = !hasTop3 && !hasAnyLeader;
  // Backend tags the response with `source: "computed"` when it had to
  // build the ranking from raw deals/leads/activity because no one has
  // awarded competition points yet this week. Show a quiet hint so the
  // CEO knows the numbers are derived, not tracked.
  const isComputed = data.source === 'computed';

  return (
    <Card className={darkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className={`text-base flex items-center gap-2 flex-wrap ${darkMode ? 'text-white' : ''}`}>
            <Trophy size={18} className="text-amber-500" />
            Competition Leaderboard
            <HelpInfo
              align="inline"
              title="How to read the Competition Leaderboard"
              steps={[
                'Weekly top-3 across the sales floor — gamification points awarded by your team for closed deals, lead conversions, and AI challenges.',
                'The countdown badge shows time left in the current competition week (resets every Monday).',
                'Below the top-3, each named bucket (Top Closer / Lead Machine / Overachiever) shows that specific competition\'s leader.',
                <>If no points have been awarded yet, the board falls back to a <em>computed</em> ranking from raw deals + activity — flagged with a small "derived" badge.</>,
              ]}
              tip="Make a habit of mentioning the top-3 in your weekly all-hands — the leaderboard becomes more motivating the more it's referenced."
            />
          </CardTitle>
          <div className="flex items-center gap-2">
            {isComputed && !isEmpty && (
              <Badge variant="outline" className="text-xs text-slate-500 border-slate-300">
                derived from this week's activity
              </Badge>
            )}
            <Badge variant="outline" className={darkMode ? 'text-slate-400' : ''}>
              <Clock size={12} className="mr-1" />
              {data.countdown?.display}
            </Badge>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {isEmpty && (
          <div className={`flex flex-col items-center justify-center py-10 text-center ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            <Trophy size={32} className="text-slate-300 mb-2" />
            <p className="text-sm font-medium">No leaderboard data yet this week</p>
            <p className="text-xs mt-1 max-w-xs">
              Closed deals, lead conversions, and activity logs will fill the leaderboard automatically.
            </p>
          </div>
        )}
        {/* Top 3 */}
        <div className="space-y-2">
          {data.top_3?.map((agent, idx) => (
            <div 
              key={agent.agent_id}
              className={`flex items-center justify-between p-3 rounded-lg ${
                idx === 0 
                  ? darkMode ? 'bg-amber-900/20 border border-amber-700/50' : 'bg-amber-50 border border-amber-200'
                  : darkMode ? 'bg-slate-800/50' : 'bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                  idx === 0 ? 'bg-amber-500 text-white' :
                  idx === 1 ? 'bg-slate-400 text-white' :
                  'bg-amber-700 text-white'
                }`}>
                  {idx === 0 ? <Crown size={16} /> : idx + 1}
                </span>
                <span className={darkMode ? 'text-white' : 'text-slate-900'}>
                  {agent.agent_name}
                  {idx === 0 && <span className="ml-2 text-amber-500">Agent of the Week 👑</span>}
                </span>
              </div>
              <span className={`font-bold ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                {agent.total_points} pts
              </span>
            </div>
          ))}
        </div>
        
        {/* Per-competition Leaders */}
        <div className="grid grid-cols-3 gap-2">
          {data.competition_leaders?.deal_closer && (
            <div className={`p-2 rounded text-center ${darkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
              <Trophy size={16} className="mx-auto text-amber-500 mb-1" />
              <p className={`text-xs font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {data.competition_leaders.deal_closer.agent_name}
              </p>
              <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Top Closer 🏆
              </p>
            </div>
          )}
          {data.competition_leaders?.lead_converter && (
            <div className={`p-2 rounded text-center ${darkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
              <Zap size={16} className="mx-auto text-blue-500 mb-1" />
              <p className={`text-xs font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {data.competition_leaders.lead_converter.agent_name}
              </p>
              <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Lead Machine ⚡
              </p>
            </div>
          )}
          {data.competition_leaders?.ai_challenge && (
            <div className={`p-2 rounded text-center ${darkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
              <Target size={16} className="mx-auto text-purple-500 mb-1" />
              <p className={`text-xs font-medium ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {data.competition_leaders.ai_challenge.agent_name}
              </p>
              <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Overachiever 🎯
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Parse a backend ISO timestamp safely. The voice-message endpoint returns
// timestamps without a timezone marker (Mongo strips tz, FastAPI emits
// naive ISO). Browsers vary on whether they treat that as UTC or local —
// we normalise by appending Z when the marker is missing so every screen
// shows the same instant.
const parseAsUTC = (raw) => {
  if (!raw) return null;
  if (raw instanceof Date) return raw;
  const s = String(raw);
  const hasTimezone = /Z$|[+-]\d{2}:?\d{2}$/.test(s);
  return new Date(hasTimezone ? s : `${s}Z`);
};

// ============ CEO Comments Section ============
const CEOCommentsSection = ({ comments, onSendComment, onMarkRead, onVoiceSent, token }) => {
  const [darkMode, setDarkMode] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [linkedTo, setLinkedTo] = useState(null); // Changed to object
  const [priority, setPriority] = useState('normal');
  const [targetHod, setTargetHod] = useState('all');
  const [sending, setSending] = useState(false);
  // Recipient toggle — same composer can address the HOD bench OR a single
  // agent. Agent mode posts to a different endpoint and uses a different
  // chat_type for the voice recorder.
  const [recipientType, setRecipientType] = useState('hod'); // 'hod' | 'agent'
  const [agents, setAgents] = useState([]);
  const [targetAgentId, setTargetAgentId] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    const checkDark = () => setDarkMode(document.documentElement.classList.contains('dark'));
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Lazy-load agent roster the first time the user switches to any
  // agent-targeting mode (single agent, All Agents broadcast, or the
  // Everyone fan-out that hits both HODs and agents). Use the wider
  // /messageable-agents feed so test agents and modern role names
  // (sales_agent / marketing_agent / cr_agent) are included.
  useEffect(() => {
    if (recipientType === 'hod' || !token) return;
    if (agents.length) return;
    (async () => {
      try {
        let list = [];
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hod/messageable-agents`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          list = data?.data?.agents || [];
        }
        if (!list.length) {
          const res2 = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hod/agent-status`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res2.ok) {
            const data2 = await res2.json();
            list = data2?.data?.agents || [];
          }
        }
        setAgents(list);
      } catch (e) { /* non-fatal */ }
    })();
  }, [recipientType, token, agents.length]);

  const handleSend = async () => {
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }
    if (recipientType === 'agent' && !targetAgentId) {
      toast.error('Select an agent name');
      return;
    }
    if ((recipientType === 'all_agents' || recipientType === 'everyone') && agents.length === 0) {
      toast.error('No agents loaded yet');
      return;
    }
    setSending(true);
    try {
      if (recipientType === 'hod') {
        await onSendComment({
          comment_text: newComment,
          linked_to: linkedTo,
          priority,
          target_hod: targetHod,
        });
      } else if (recipientType === 'everyone') {
        // Fan the same comment out to BOTH the HOD bench (via the
        // canonical CEO comments endpoint, which now also lands in each
        // HOD's My Dashboard feedback feed) AND every agent (via the
        // direct-message endpoint each agent's feedback panel already
        // consumes). One CEO action → one message visible to everyone.
        const hodCall = onSendComment({
          comment_text: newComment,
          linked_to: linkedTo,
          priority,
          target_hod: 'all',
        });
        const agentCalls = Promise.allSettled(agents.map((a) => {
          const id = a.id || a.agent_id;
          return fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/hod/agents/${id}/direct-message`,
            {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ feedback_text: newComment, agent_id: id }),
            },
          ).then((r) => r.json());
        }));
        const [, agentResults] = await Promise.all([hodCall, agentCalls]);
        const ok = agentResults.filter((r) => r.status === 'fulfilled' && r.value?.success).length;
        toast.success(`Broadcast sent to all HODs + ${ok} of ${agents.length} agents`);
      } else if (recipientType === 'all_agents') {
        const results = await Promise.allSettled(agents.map((a) => {
          const id = a.id || a.agent_id;
          return fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/hod/agents/${id}/direct-message`,
            {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ feedback_text: newComment, agent_id: id }),
            },
          ).then((r) => r.json());
        }));
        const ok = results.filter((r) => r.status === 'fulfilled' && r.value?.success).length;
        toast.success(`Broadcast sent to ${ok} of ${agents.length} agents`);
      } else {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/hod/agents/${targetAgentId}/direct-message`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ feedback_text: newComment, agent_id: targetAgentId }),
          },
        );
        const data = await res.json();
        if (!data.success) { toast.error(data.detail || 'Failed to send'); return; }
        toast.success('Comment sent to agent');
      }
      setNewComment('');
      setLinkedTo(null);
      setPriority('normal');
      setTargetHod('all');
    } finally {
      setSending(false);
    }
  };

  const onFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (recipientType === 'agent' && !targetAgentId) { toast.error('Select an agent name'); return; }
    if ((recipientType === 'all_agents' || recipientType === 'everyone') && agents.length === 0) {
      toast.error('No agents loaded yet'); return;
    }
    if (!file.type.startsWith('audio/')) { toast.error('Pick an audio file'); return; }
    setUploadingFile(true);
    try {
      const dur = await new Promise((resolve) => {
        const audio = new Audio();
        audio.preload = 'metadata';
        audio.onloadedmetadata = () => resolve(Math.round(audio.duration) || 0);
        audio.onerror = () => resolve(0);
        audio.src = URL.createObjectURL(file);
      });

      const post = async (extras) => {
        const fd = new FormData();
        fd.append('audio', file, file.name);
        fd.append('duration_seconds', String(dur));
        Object.entries(extras).forEach(([k, v]) => fd.append(k, v));
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/voice-messages/upload?token=${encodeURIComponent(token)}`,
          { method: 'POST', body: fd },
        );
        return res.json();
      };

      if (recipientType === 'hod') {
        const data = await post({ chat_type: 'hod_comments', target_hod: targetHod });
        if (data.success) { toast.success('Voice file sent'); if (onVoiceSent) onVoiceSent(data.message); }
        else toast.error(data.error || 'Upload failed');
      } else if (recipientType === 'everyone') {
        // Mirror the text-send path: voice file goes to the HOD bench
        // AND every agent in parallel so the audio lands in every inbox.
        const hodCall = post({ chat_type: 'hod_comments', target_hod: 'all' });
        const agentCalls = Promise.allSettled(agents.map((a) =>
          post({ chat_type: 'agent_feedback', contact_id: a.id || a.agent_id })
        ));
        const [hodData, agentResults] = await Promise.all([hodCall, agentCalls]);
        const ok = agentResults.filter((r) => r.status === 'fulfilled' && r.value?.success).length;
        const hodOk = !!hodData?.success;
        if (hodData?.success && onVoiceSent) onVoiceSent(hodData.message);
        toast.success(`Voice file sent to ${hodOk ? 'all HODs' : 'HODs failed'} + ${ok} of ${agents.length} agents`);
      } else if (recipientType === 'all_agents') {
        const results = await Promise.allSettled(agents.map((a) =>
          post({ chat_type: 'agent_feedback', contact_id: a.id || a.agent_id })
        ));
        const ok = results.filter((r) => r.status === 'fulfilled' && r.value?.success).length;
        toast.success(`Voice file broadcast to ${ok} of ${agents.length} agents`);
      } else {
        const data = await post({ chat_type: 'agent_feedback', contact_id: targetAgentId });
        if (data.success) { toast.success('Voice file sent'); if (onVoiceSent) onVoiceSent(data.message); }
        else toast.error(data.error || 'Upload failed');
      }
    } catch (e) {
      toast.error(`Upload failed: ${e.message || e}`);
    } finally {
      setUploadingFile(false);
    }
  };

  const getTargetLabel = (target) => {
    switch(target) {
      case 'hod_sales': return 'HOD Sales';
      case 'hod_marketing': return 'HOD Marketing';
      default: return 'All HODs';
    }
  };
  
  return (
    <Card className={darkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}>
      <CardHeader className="pb-3">
        <CardTitle className={`text-base flex items-center gap-2 flex-wrap ${darkMode ? 'text-white' : ''}`}>
          <MessageSquare size={18} className="text-blue-500" />
          {recipientType === 'hod'
            ? 'Comments to HOD'
            : recipientType === 'everyone'
              ? 'Broadcast to Everyone'
              : recipientType === 'all_agents'
                ? 'Broadcast to All Agents'
                : 'Comments to Agent'}
          <HelpInfo
            align="inline"
            title="How to use this composer"
            steps={[
              <>Pick a recipient type on the left — <em>HOD</em>, <em>All Agents</em>, <em>Everyone</em>, or a single <em>Agent</em>. The second dropdown narrows it (e.g. a specific HOD by name, or one agent).</>,
              'Use Priority = Urgent when you want the recipient to see a red badge and a louder bell — reserve it for time-sensitive instructions.',
              'Text and voice can go through the same recipient picker. Click the mic to record live, or Upload audio to send a pre-recorded clip.',
              <>Single-recipient messages land in the recipient's <em>Feedback from Management</em> panel; HOD messages also land in the HOD's CEO Comments thread.</>,
              'Broadcasts fan out one message per agent so each can reply independently — you\'ll see each thread in your CEO Comments feed.',
            ]}
            tip="Voice notes feel more direct than text. Use them for praise or coaching where tone matters; keep text for action items you want a paper trail of."
          />
        </CardTitle>
        <CardDescription className={darkMode ? 'text-slate-400' : ''}>
          {recipientType === 'hod'
            ? 'Private communication channel - Select which HOD to message'
            : recipientType === 'all_agents'
              ? 'Send the same message or voice note to every agent on the team'
              : 'Send a private comment or voice note to one specific agent'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* New Comment Form */}
        <div className={`p-4 rounded-lg ${darkMode ? 'bg-slate-800/50' : 'bg-slate-50'}`}>
          <Textarea
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder={
              recipientType === 'agent'
                ? 'Write a private comment for this agent…'
                : 'Write a comment or instruction for HOD...'
            }
            rows={3}
            className="mb-3"
            data-testid="ceo-comment-input"
          />

          {/* Recipient toggle — HODs / Everyone / All Agents / Agent */}
          <div className="mb-3 flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Send to
            </span>
            <Select value={recipientType} onValueChange={(v) => { setRecipientType(v); setTargetAgentId(''); }}>
              <SelectTrigger
                className={`h-9 w-[220px] ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-200'}`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hod">HODs</SelectItem>
                {/* "Everyone" fans the same comment out to every HOD AND
                    every agent in one click. Useful for company-wide
                    announcements that previously needed two sends. */}
                <SelectItem value="everyone">Everyone (HODs + Agents)</SelectItem>
                <SelectItem value="all_agents">All Agents</SelectItem>
                <SelectItem value="agent">Agent</SelectItem>
              </SelectContent>
            </Select>
            {recipientType === 'agent' && (
              <div className="flex items-center gap-1.5">
                <Select value={targetAgentId} onValueChange={setTargetAgentId}>
                  <SelectTrigger
                    className={`h-9 w-[240px] ${
                      !targetAgentId ? 'border-red-400 ring-1 ring-red-200' : ''
                    } ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-200'}`}
                  >
                    <SelectValue placeholder="Select an agent (required)" />
                  </SelectTrigger>
                  <SelectContent>
                    {agents.length === 0 && (
                      <SelectItem value="__none__" disabled>No agents found</SelectItem>
                    )}
                    {agents.map((a) => (
                      <SelectItem key={a.id || a.agent_id} value={a.id || a.agent_id}>
                        {a.full_name || a.agent_name || a.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-red-500 text-xs font-semibold" title="Required">*</span>
              </div>
            )}
            {recipientType === 'all_agents' && (
              <span className="text-xs text-slate-600 px-2 py-1 rounded bg-slate-100 border border-slate-200">
                {agents.length} agent{agents.length === 1 ? '' : 's'} will receive this
              </span>
            )}
            {recipientType === 'everyone' && (
              <span className="text-xs text-slate-600 px-2 py-1 rounded bg-violet-50 border border-violet-200">
                All HODs + {agents.length} agent{agents.length === 1 ? '' : 's'} will receive this
              </span>
            )}
          </div>

          {/* Linked To Selector — only meaningful when targeting an HOD */}
          {recipientType === 'hod' && (
            <div className="mb-3">
              <label className={`block text-xs font-medium mb-1.5 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Link to Record (Optional)
              </label>
              <LinkedToSelector
                value={linkedTo}
                onChange={setLinkedTo}
                token={token}
              />
            </div>
          )}

          <div className="flex items-center gap-4 flex-wrap">
            {recipientType === 'hod' && (
              <>
                <Select value={targetHod} onValueChange={setTargetHod}>
                  <SelectTrigger
                    className={`h-10 w-[150px] ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-200'}`}
                    data-testid="target-hod-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All HODs</SelectItem>
                    <SelectItem value="hod_sales">HOD Sales</SelectItem>
                    <SelectItem value="hod_marketing">HOD Marketing</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger
                    className={`h-10 w-[120px] ${darkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-200'}`}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </>
            )}
            <Button
              onClick={handleSend}
              disabled={
                sending
                || (recipientType === 'agent' && !targetAgentId)
                || (recipientType === 'all_agents' && agents.length === 0)
              }
              data-testid="send-comment-btn"
            >
              {sending ? <RefreshCw size={16} className="animate-spin" /> : <Send size={16} />}
            </Button>
            {recipientType === 'hod' ? (
              <VoiceMessageRecorder
                chatType="hod_comments"
                targetHod={targetHod}
                compact
                onSend={onVoiceSent}
              />
            ) : recipientType === 'all_agents' ? (
              <VoiceMessageRecorder
                chatType="agent_feedback"
                contactId={(agents[0]?.id || agents[0]?.agent_id) || null}
                disabled={agents.length === 0}
                disabledReason="No agents loaded — wait a moment, then try again"
                compact
                onSend={onVoiceSent}
              />
            ) : (
              <VoiceMessageRecorder
                chatType="agent_feedback"
                contactId={targetAgentId || null}
                disabled={!targetAgentId}
                disabledReason="Select an agent name first"
                compact
                onSend={onVoiceSent}
              />
            )}
            <label className="inline-flex">
              <input
                type="file"
                accept="audio/*"
                hidden
                disabled={uploadingFile || (recipientType === 'agent' && !targetAgentId)}
                onChange={onFileUpload}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={uploadingFile || (recipientType === 'agent' && !targetAgentId)}
                onClick={(e) => e.currentTarget.previousSibling.click()}
              >
                {uploadingFile ? <RefreshCw size={14} className="animate-spin mr-1" /> : null}
                Upload audio
              </Button>
            </label>
          </div>
        </div>
        
        {/* Comments History */}
        <ScrollArea className="h-[300px]">
          <div className="space-y-3 pr-4">
            {comments?.map((comment) => comment._kind === 'voice' ? (
              <div
                key={`voice-${comment.id}`}
                className={`p-4 rounded-lg border ${
                  darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'
                }`}
              >
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Mic size={14} className="text-purple-500" />
                  <span className={`text-xs font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                    Voice note
                  </span>
                  <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {Math.round(comment.duration_seconds || 0)}s
                  </span>
                  {comment.target_hod && (
                    <Badge
                      variant="secondary"
                      className={`text-xs ${
                        comment.target_hod === 'hod_sales' ? 'bg-blue-100 text-blue-700' :
                        comment.target_hod === 'hod_marketing' ? 'bg-purple-100 text-purple-700' :
                        'bg-slate-100 text-slate-700'
                      }`}
                    >
                      → {getTargetLabel(comment.target_hod)}
                    </Badge>
                  )}
                </div>
                <audio
                  controls
                  preload="none"
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/voice-messages/${comment.id}/audio`}
                  className="w-full"
                />
                <div className={`text-xs mt-2 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {parseAsUTC(comment.created_at)?.toLocaleString() || ''}
                </div>
              </div>
            ) : (
              <div
                key={comment.id}
                className={`p-4 rounded-lg border ${
                  comment.priority === 'urgent'
                    ? darkMode ? 'bg-red-900/20 border-red-700/50' : 'bg-red-50 border-red-200'
                    : darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      {/* Linked Record Display */}
                      {comment.linked_to && typeof comment.linked_to === 'object' && comment.linked_to.recordLabel ? (
                        <LinkedToDisplayChip
                          linkedTo={comment.linked_to}
                          onClick={(linked) => handleCEOLinkedRecordClick(linked)}
                        />
                      ) : comment.linked_to && typeof comment.linked_to === 'string' ? (
                        // Legacy: plain string linked_to
                        <Badge variant="outline" className="text-xs">
                          {comment.linked_to}
                        </Badge>
                      ) : null}
                      <Badge
                        variant="secondary"
                        className={`text-xs ${
                          comment.target_hod === 'hod_sales'
                            ? 'bg-blue-100 text-blue-700'
                            : comment.target_hod === 'hod_marketing'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        → {getTargetLabel(comment.target_hod || 'all')}
                      </Badge>
                      {/* Sender name — show who sent this original message
                          so the CEO can quickly tell CEO vs another admin. */}
                      {(comment.ceo_name || comment.sender_name || comment.author_name) && (
                        <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          · Sent by{' '}
                          <span className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                            {comment.ceo_name || comment.sender_name || comment.author_name}
                          </span>
                        </span>
                      )}
                    </div>
                    <p className={darkMode ? 'text-white' : 'text-slate-900'}>
                      {comment.comment_text}
                    </p>
                  </div>
                  <Badge variant={comment.priority === 'urgent' ? 'destructive' : 'secondary'} className="text-xs">
                    {comment.priority}
                  </Badge>
                </div>
                <div className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {new Date(comment.created_at).toLocaleString()}
                  {comment.is_read_by_hod ? (
                    <span className="ml-2 text-emerald-500">
                      ✓ Seen by {comment.replied_by_role ? getTargetLabel(comment.replied_by_role) : 'HOD'}
                    </span>
                  ) : (
                    <span className="ml-2 text-amber-500">Pending</span>
                  )}
                </div>
                
                {/* Reply thread — full conversation array is the new
                    canonical source. Falls back to the legacy single
                    `hod_reply` string for old rows that pre-date the
                    array field. */}
                {Array.isArray(comment.conversation) && comment.conversation.length > 0 ? (
                  <div className={`mt-3 space-y-2 pl-3 ${darkMode ? 'border-l-2 border-slate-600' : 'border-l-2 border-slate-300'}`}>
                    {comment.conversation.map((msg, idx) => {
                      const isCeoReply = msg.sender_role === 'ceo' || msg.sender_role === 'super_admin';
                      const label = isCeoReply
                        ? 'CEO Reply'
                        : msg.sender_role === 'hod_sales'
                            ? 'HOD Sales Reply'
                            : msg.sender_role === 'hod_marketing'
                                ? 'HOD Marketing Reply'
                                : 'HOD Reply';
                      // Reply timestamps come from different fields
                      // depending on the row's age: `created_at`, `sent_at`
                      // or `timestamp`. Only render the line when we
                      // actually get a parseable value — otherwise
                      // Date(undefined) shows "Invalid Date".
                      const rawTs = msg.created_at || msg.sent_at || msg.timestamp || msg.replied_at;
                      const parsed = rawTs ? new Date(rawTs) : null;
                      const tsStr = parsed && !Number.isNaN(parsed.getTime())
                        ? parsed.toLocaleString()
                        : null;
                      return (
                        <div
                          key={msg.id || idx}
                          className={`p-2.5 rounded text-sm ${
                            isCeoReply
                              ? darkMode
                                  ? 'bg-violet-900/30 border border-violet-700/50 text-violet-100'
                                  : 'bg-violet-50 border border-violet-200 text-slate-800'
                              : darkMode
                                  ? 'bg-slate-700/50 border border-slate-600 text-slate-100'
                                  : 'bg-slate-100 border border-slate-200 text-slate-800'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                            <span className={`text-xs font-semibold ${
                              isCeoReply
                                ? darkMode ? 'text-violet-300' : 'text-violet-700'
                                : darkMode ? 'text-slate-300' : 'text-slate-700'
                            }`}>
                              {label}
                            </span>
                            {(msg.sender_name || msg.author_name) && (
                              <span className={`text-[11px] ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                · from{' '}
                                <span className={`font-medium ${darkMode ? 'text-slate-200' : 'text-slate-700'}`}>
                                  {msg.sender_name || msg.author_name}
                                </span>
                              </span>
                            )}
                          </div>
                          <p className="whitespace-pre-wrap">{msg.text || msg.message || ''}</p>
                          {tsStr && (
                            <p className={`text-[11px] mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                              {tsStr}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : comment.hod_reply ? (
                  <div className={`mt-3 p-3 rounded ${darkMode ? 'bg-slate-700/50' : 'bg-slate-100'}`}>
                    <p className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                      <strong>HOD replied:</strong> {comment.hod_reply}
                    </p>
                    <p className={`text-xs mt-1 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      {new Date(comment.hod_replied_at).toLocaleString()}
                    </p>
                    {!comment.is_ceo_read_reply && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 h-auto text-xs"
                        onClick={() => onMarkRead(comment.id)}
                      >
                        Mark as read
                      </Button>
                    )}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

// ============ Agent Report Modal ============
const AgentReportModal = ({ agent, report, isOpen, onClose, token }) => {
  const [darkMode, setDarkMode] = useState(false);
  // Inline feedback composer state. `mode` null = the button is shown;
  // 'feedback' opens the textarea + audience toggle below the report.
  const [mode, setMode] = useState(null);
  // 'agent' = direct message to the agent only; 'hod' = note to their
  // HOD via the CEO Comments thread; 'both' = fan out to both at once.
  const [audience, setAudience] = useState('both');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const checkDark = () => setDarkMode(document.documentElement.classList.contains('dark'));
    checkDark();
  }, []);

  // Reset the composer whenever the modal closes or the agent changes, so
  // the next open starts fresh instead of with the prior message draft.
  useEffect(() => {
    if (!isOpen) {
      setMode(null);
      setText('');
      setAutoText('');
      setAudience('both');
    }
  }, [isOpen, agent?.agent_id]);

  // Best-effort department inference. Backend team payloads sometimes
  // carry `department` directly, sometimes only `role` (sales_agent vs
  // marketing_agent vs cr_agent). Falling back on role keeps the HOD
  // routing accurate when department isn't on the row.
  const inferredDept = (() => {
    const d = (agent?.department || '').toLowerCase();
    if (d.includes('market')) return 'Marketing';
    if (d.includes('sales')) return 'Sales';
    const r = (agent?.role || '').toLowerCase();
    if (r.includes('market')) return 'Marketing';
    if (r.includes('sales') || r === 'cr_agent') return 'Sales';
    return '';
  })();
  const targetHod = inferredDept === 'Marketing' ? 'hod_marketing'
                  : inferredDept === 'Sales' ? 'hod_sales'
                  : 'all';

  // Pull a sensible greeting name. Most users have a real first name
  // (e.g., "John Smith" → "John"), but generic / test accounts like
  // "Sales Agent Test" would produce a weird "Hi Sales,". For those
  // we fall back to the full name so the opener still reads naturally.
  const greetingName = (() => {
    const name = (agent?.agent_name || '').trim();
    if (!name) return 'there';
    const first = name.split(/\s+/)[0];
    const genericFirsts = new Set(['sales', 'marketing', 'cr', 'hod', 'agent', 'test']);
    return genericFirsts.has(first.toLowerCase()) ? name : first;
  })();

  // Audience-specific prefill. Each template is built to land with
  // context even if the CEO sends it as-is: it references concrete
  // things a daily report typically covers (pipeline, leads, follow-ups,
  // field activity) and asks for something specific back. The CEO can
  // still edit before sending — this just makes the cold-start useful
  // instead of a vague "wanted to share a quick note".
  const composeText = (aud) => {
    const dept = inferredDept || 'the team';
    const reportRef = report?.generated_at
      ? `today's daily report (${new Date(report.generated_at).toLocaleDateString()})`
      : 'your latest daily report';

    if (aud === 'agent') {
      return (
        `Hi ${greetingName},\n\n` +
        `I went through ${reportRef}. A few things stood out and I want ` +
        `to share my read on them:\n\n` +
        `1) Pipeline — anything sitting in negotiation longer than a week needs a ` +
        `concrete next step this week. Tell me what's blocking the top one and ` +
        `what you need from me or ${inferredDept ? `the ${inferredDept} HOD` : 'your HOD'} to move it.\n` +
        `2) Leads — protect tomorrow morning for first-touch on anything that didn't ` +
        `get contacted today. Cold leads are the easiest losses to avoid.\n` +
        `3) Follow-ups — close out the overdue items first. They're the quickest ` +
        `trust-builders with clients.\n\n` +
        `Reply here with your plan for tomorrow and any deals you want me involved ` +
        `in directly. I'm in your corner — let's make the next report a stronger one.\n\n` +
        `— CEO`
      );
    }

    if (aud === 'hod') {
      return (
        `Reviewing ${reportRef} from ${agent?.agent_name || 'the agent'} ` +
        `(${inferredDept || 'team'}). A few observations for you:\n\n` +
        `• Pipeline velocity looks soft — deals in negotiation aren't progressing ` +
        `fast enough. I'd like your read on which ones are real and which need to ` +
        `be re-qualified or dropped.\n` +
        `• Lead engagement is light today. If this is a pattern across the team, ` +
        `we need a coaching intervention, not just a nudge to the agent.\n` +
        `• Follow-ups are slipping. Please make sure ${greetingName} clears the ` +
        `overdue list this week and that you're spot-checking the rest of ${dept}.\n\n` +
        `Reply with your plan for the next 48 hours — both for this agent and for ` +
        `any team-wide gaps you're seeing in the same shape.\n\n` +
        `— CEO`
      );
    }

    // both — speaks to the agent and HOD on the same message so they
    // see each other's accountability.
    return (
      `${greetingName} (and ${inferredDept ? `HOD ${inferredDept}` : 'HOD'}),\n\n` +
      `I just reviewed ${reportRef}. Sending this to you both so we're aligned ` +
      `on what good looks like for the next 48 hours:\n\n` +
      `For ${greetingName}:\n` +
      `• Identify the top deal in negotiation, name the blocker, and own the next ` +
      `step before end of day tomorrow.\n` +
      `• Re-engage any leads that didn't get a first touch today — quick wins are ` +
      `still wins.\n` +
      `• Clear the overdue follow-up list. Don't let trust leak from small things.\n\n` +
      `For ${inferredDept ? `HOD ${inferredDept}` : 'HOD'}:\n` +
      `• Sit with ${greetingName} on the top pipeline item this week and unblock it.\n` +
      `• If the same pattern is showing up across the team, bring me a coaching ` +
      `plan, not a status update.\n\n` +
      `Reply on this thread with a concrete plan. I'm watching for momentum, not ` +
      `excuses — but I'm also here to help where it matters.\n\n` +
      `— CEO`
    );
  };

  // Prefill — sets the initial body based on the default audience so the
  // CEO can hit send immediately when the template matches. Tracks the
  // auto-text so changing the audience refreshes the body only when the
  // user hasn't typed over it.
  const [autoText, setAutoText] = useState('');
  const startFeedback = () => {
    setMode('feedback');
    const t = composeText(audience);
    setText(t);
    setAutoText(t);
  };

  // When the CEO switches audience after opening the composer, regenerate
  // the prefill to match — but only if they haven't manually edited it,
  // so we don't clobber their words.
  const switchAudience = (next) => {
    setAudience(next);
    if (text === autoText) {
      const t = composeText(next);
      setText(t);
      setAutoText(t);
    }
  };

  const cancel = () => { setMode(null); setText(''); setAutoText(''); };

  // Direct message to the agent uses the same /api/hod/agents/.../direct-message
  // endpoint the AlertActions feedback path uses, so it appears in the
  // agent's inbound feedback box exactly like an HOD-sent note would.
  const sendToAgent = () => {
    if (!agent?.agent_id) return Promise.resolve({ success: false });
    return fetch(`${API_URL}/api/hod/agents/${agent.agent_id}/direct-message`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ feedback_text: text, agent_id: agent.agent_id }),
    }).then((r) => r.json());
  };

  // CEO → HOD note threaded against this agent so the HOD can see the
  // report context in their CEO Comments tab.
  const sendToHod = () => fetch(`${API_URL}/api/ceo/comments`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      comment_text: text,
      target_hod: targetHod,
      priority: 'normal',
      linked_to: {
        recordType: 'agent',
        recordId: agent?.agent_id,
        recordLabel: agent?.agent_name,
      },
    }),
  }).then((r) => r.json());

  const send = async () => {
    if (!text.trim()) { toast.error('Add a message before sending'); return; }
    setSending(true);
    try {
      if (audience === 'agent') {
        const data = await sendToAgent();
        if (!data?.success) { toast.error(data?.error || data?.detail || 'Failed to send'); return; }
        toast.success(`Feedback sent to ${agent?.agent_name}`);
      } else if (audience === 'hod') {
        const data = await sendToHod();
        if (!data?.success) { toast.error(data?.error || data?.detail || 'Failed to send'); return; }
        toast.success(`Feedback sent to HOD ${inferredDept || ''}`.trim());
      } else {
        // 'both' — agent + their HOD in parallel
        const [agentRes, hodRes] = await Promise.all([sendToAgent(), sendToHod()]);
        const ok = !!agentRes?.success;
        const hodOk = !!hodRes?.success;
        toast.success(
          ok && hodOk
            ? `Feedback sent to ${agent?.agent_name} + HOD`
            : ok
              ? `Feedback sent to ${agent?.agent_name} (HOD failed)`
              : hodOk
                ? `Feedback sent to HOD (agent failed)`
                : 'Both deliveries failed'
        );
      }
      cancel();
    } catch (e) {
      toast.error(`Send failed: ${e.message || e}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`max-w-2xl p-0 ${darkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}`}>
        <DialogHeader>
          <DialogTitle className={darkMode ? 'text-white' : ''}>
            Agent Report: {agent?.agent_name}
            {inferredDept && (
              <Badge variant="outline" className="ml-2 text-xs">{inferredDept}</Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="h-[500px]">
          {report ? (
            <div className={`prose prose-sm max-w-none p-4 ${darkMode ? 'prose-invert' : ''}`}>
              <div dangerouslySetInnerHTML={{
                __html: report.report_text
                  ?.replace(/\n/g, '<br/>')
                  .replace(/## /g, '<h3>')
                  .replace(/- /g, '&bull; ')
              }} />
              <p className={`text-xs mt-4 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                Generated: {new Date(report.generated_at).toLocaleString()} • v{report.version}
              </p>
            </div>
          ) : (
            <div className={`text-center py-12 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              <FileText size={48} className="mx-auto mb-4 opacity-50" />
              <p>No report available for this agent</p>
            </div>
          )}
        </ScrollArea>

        {/* Feedback footer — collapsed by default to a single button so
            it doesn't compete with the report content. Expands inline
            with a textarea + audience toggle when clicked. */}
        <DialogFooter className={`flex-col items-stretch ${darkMode ? 'bg-slate-900/40' : ''}`}>
          {mode !== 'feedback' ? (
            <div className="flex justify-end w-full">
              <Button
                size="sm"
                className="bg-violet-600 hover:bg-violet-700 text-white"
                onClick={startFeedback}
                disabled={!token || !agent?.agent_id}
              >
                <MessageSquare size={14} className="mr-1.5" />
                Send Feedback
              </Button>
            </div>
          ) : (
            <div className="space-y-2 w-full">
              <div className="flex items-center gap-2 flex-wrap">
                <p className={`text-xs font-semibold text-emerald-600`}>
                  Sending feedback to {audience === 'agent'
                    ? agent?.agent_name
                    : audience === 'hod'
                      ? `HOD ${inferredDept || ''}`.trim()
                      : `${agent?.agent_name} + HOD ${inferredDept || ''}`.trim()}
                </p>
                <div className="ml-auto flex items-center gap-1 flex-wrap">
                  <span className="text-xs text-slate-500">Send to:</span>
                  {[
                    { v: 'both', label: 'Agent + HOD' },
                    { v: 'agent', label: 'Agent only' },
                    { v: 'hod', label: 'HOD only' },
                  ].map((opt) => (
                    <Button
                      key={opt.v}
                      size="sm"
                      variant={audience === opt.v ? 'default' : 'outline'}
                      className={`h-6 text-xs px-2 ${audience === opt.v ? 'bg-violet-600 hover:bg-violet-700 text-white' : ''}`}
                      onClick={() => switchAudience(opt.v)}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </div>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                placeholder="Write your feedback to this agent…"
                className="text-sm"
              />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={cancel} disabled={sending}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  onClick={send}
                  disabled={sending || !text.trim()}
                >
                  {sending ? (
                    <><RefreshCw size={13} className="mr-1 animate-spin" /> Sending</>
                  ) : (
                    <><Send size={13} className="mr-1" /> Send Feedback</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ Main CEO Dashboard ============
const CEODashboard = () => {
  const { token, user } = useAuth();
  const { isDarkMode } = useTheme();
  
  // Dark mode detection
  const [darkMode, setDarkMode] = useState(isDarkMode);
  useEffect(() => {
    const checkDark = () => setDarkMode(document.documentElement.classList.contains('dark') || window.matchMedia('(prefers-color-scheme: dark)').matches);
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [isDarkMode]);
  
  // State
  const [brief, setBrief] = useState(null);
  const [salesSnapshot, setSalesSnapshot] = useState(null);
  const [marketingSnapshot, setMarketingSnapshot] = useState(null);
  const [salesTeam, setSalesTeam] = useState([]);
  const [marketingTeam, setMarketingTeam] = useState([]);
  const [competition, setCompetition] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshingBrief, setRefreshingBrief] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  // ---- Dashboard scope controls -----------------------------------------
  // Same control set the HOD dashboard uses — surfaced here so the CEO
  // can flip between the org-wide picture, a single team's view, and
  // the personal "what does MY desk look like" lens, and pivot widgets
  // between agents / sources / campaigns / projects. Currently
  // informational; consumed by downstream widgets in subsequent
  // iterations.
  // Default to "all" — the unified org-wide view. The dropdown also
  // accepts "hod_sales", "hod_marketing", "agents_overview", and any
  // string of the form "agent:<id>" for a specific agent drill-in.
  const [dashboardScope, setDashboardScope] = useState('all');
  const [sliceBy, setSliceBy] = useState('agents');
  const [withTeam, setWithTeam] = useState(false);

  // Agent roster for the population dropdown. Pulled once from
  // /api/users and filtered down to active agent / sales / marketing
  // roles. Empty until the fetch completes; the dropdown still works
  // without it (the four fixed options are always rendered).
  const [agentRoster, setAgentRoster] = useState([]);
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        // The "list users in my account" route lives under
        // /api/accounts/users (account_routes.py has prefix
        // `/api/accounts`). The bare /api/users path returns 404.
        const r = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/accounts/users`,
          { params: { token } },
        );
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data
                   : Array.isArray(r.data?.users) ? r.data.users
                   : Array.isArray(r.data?.data) ? r.data.data
                   : [];
        // Show every active non-leadership user. We used to require
        // the role to contain "agent" / "sales" / "telesales" — but
        // that dropped real assignees like indirect_sales, bdm,
        // broker, marketing, etc. and left the dropdown empty in orgs
        // that don't use the exact "agent" suffix. Exclusion is
        // safer: drop the CEO / HOD / admin tier and keep the rest.
        const LEADERSHIP = new Set([
          'ceo', 'admin', 'super_admin', 'owner', 'account_owner',
          'hod', 'hod_sales', 'hod_marketing', 'manager',
        ]);
        const filtered = list
          .filter((u) => {
            const role = String(u.role || '').toLowerCase();
            if (LEADERSHIP.has(role)) return false;
            if (u.is_active === false || u.is_disabled === true) return false;
            return true;
          })
          .map((u) => ({
            id: u.id || u._id,
            name: u.full_name || u.email || 'Unnamed',
            role: u.role || 'agent',
          }))
          .filter((u) => !!u.id)
          .sort((a, b) => a.name.localeCompare(b.name));
        setAgentRoster(filtered);
      } catch (e) {
        // Soft-fail — the dropdown still has its four fixed options.
        console.warn('agent roster:', e?.response?.data?.detail || e);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Modal state
  const [selectedAgent, setSelectedAgent] = useState(null);

  // ---- Assign Task dialog state ----------------------------------------
  // CEO can assign a task to any agent in the org. Mirrors the HOD
  // dashboard's task-assign flow — same POST target (/api/hod/tasks)
  // because the endpoint accepts CEO + HOD roles.
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);
  // Chat modal — opens the same "Comments to HOD" composer as the
  // dedicated CEO Comments tab, but as a floating dialog so the CEO
  // can send a quick message without leaving the current tab.
  const [isChatDialogOpen, setIsChatDialogOpen] = useState(false);
  // AI Insights modal — the AI Executive Brief (Today's Briefing +
  // full markdown) used to sit inline between CEO Comments and the
  // Department Snapshots section. Moved behind the "AI Insights"
  // button in the header — one click, capsule modal, no permanent
  // real estate. Matches the pattern used on MyMarketingDashboard.
  const [isAIInsightsOpen, setIsAIInsightsOpen] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [taskForm, setTaskForm] = useState({
    agent_id:    '',
    type:        'task',
    title:       '',
    description: '',
    priority:    'medium',
    due_date:    '',
    due_time:    '',
  });
  const resetTaskForm = () => setTaskForm({
    agent_id: '', type: 'task', title: '', description: '',
    priority: 'medium', due_date: '', due_time: '',
  });
  const handleCreateTask = async () => {
    if (!taskForm.agent_id || !taskForm.title.trim()) {
      toast.error('Pick an agent and a title');
      return;
    }
    setCreatingTask(true);
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hod/tasks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(taskForm),
      });
      const result = await resp.json();
      if (result?.success) {
        toast.success('Task assigned');
        setIsTaskDialogOpen(false);
        resetTaskForm();
      } else {
        toast.error(result?.error || result?.detail || 'Failed to assign task');
      }
    } catch (e) {
      toast.error('Failed to assign task');
    } finally {
      setCreatingTask(false);
    }
  };
  const [agentReport, setAgentReport] = useState(null);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  
  // Handle department change - update view
  const handleDepartmentChange = useCallback((dept) => {
    setSelectedDepartment(dept);
  }, []);

  // ---- Drive the page from the dark scope-bar dropdowns ----------------
  // The page already has three conditional blocks keyed off
  // `selectedDepartment` ('all' / 'sales' / 'marketing'). Wire the new
  // dropdown into the same state so the existing layout reacts:
  //   - "All Dashboard"             → all
  //   - "HOD Sales Dashboard"       → sales
  //   - "HOD Marketing Dashboard"   → marketing
  //   - "Agents Dashboard Overview" → all (we'll layer the agent banner)
  //   - "agent:<id>" via sliceBy    → all + agent-drill banner
  useEffect(() => {
    if (dashboardScope === 'hod_sales')          setSelectedDepartment('sales');
    else if (dashboardScope === 'hod_marketing') setSelectedDepartment('marketing');
    // Lead Dashboard (BDM + Indirect Sales flow) — no separate department
    // slice in the CEO layout yet, so it reads as 'all' with the banner
    // above telling the CEO what they're looking at. When the HOD/Lead
    // dashboard endpoints start honouring a `scope=lead_dashboard`
    // param, swap this to setSelectedDepartment('lead_dashboard').
    else if (dashboardScope === 'lead_dashboard') setSelectedDepartment('all');
    else                                          setSelectedDepartment('all');

    // Clear any agent drill-in when the role-view pill isn't Agents
    // Dashboard Overview — the second pill is hidden in those modes
    // and a stale agent:<id> would orphan the "Now viewing" banner.
    if (dashboardScope !== 'agents_overview') {
      setSliceBy('agents');
      setWithTeam(false);
    }
  }, [dashboardScope]);

  // Resolve the currently-selected agent (if any) from the second pill.
  // Returns {id, name, role} or null.
  const selectedAgentInfo = useMemo(() => {
    const v = String(sliceBy || '');
    if (!v.startsWith('agent:')) return null;
    const id = v.slice(6);
    return agentRoster.find((a) => a.id === id) || { id, name: 'Selected agent', role: '' };
  }, [sliceBy, agentRoster]);
  
  // Navigation hook for linked record clicks
  const navigate = useNavigate();
  
  // Handle clicking on linked record chips - navigates to the relevant CRM record
  const handleCEOLinkedRecordClick = useCallback((linkedTo) => {
    if (!linkedTo || linkedTo.isDeleted) return;
    
    const { recordType, recordId } = linkedTo;
    
    switch (recordType) {
      case 'agent':
        // Navigate to team/users with the agent highlighted
        navigate(`/users?highlight=${recordId}`);
        break;
      case 'deal':
        // Navigate to analytics with deal filter
        navigate(`/analytics?deal=${recordId}`);
        break;
      case 'lead':
        // Navigate to contact detail page
        navigate(`/contacts/${recordId}`);
        break;
      case 'report':
        // Navigate to agent daily report or dashboard
        navigate(`/hod-dashboard?report=${recordId}`);
        break;
      case 'campaign':
        // Navigate to marketing campaign
        navigate(`/marketing/campaigns/${recordId}`);
        break;
      default:
        // Fallback - show toast with record info
        toast.info(`Record: ${linkedTo.recordLabel || 'Unknown'}`);
    }
  }, [navigate]);
  
  // Fetch all data. Each endpoint resolves independently and updates its
  // own state — we DON'T wait for the slowest one before rendering. This
  // was the previous bottleneck: a single slow endpoint (typically the AI
  // brief) would freeze the whole dashboard on a full-page spinner for
  // many seconds. Now the page paints as soon as any data lands, and slow
  // sections fill in progressively.
  const fetchData = useCallback(async () => {
    if (!token) return;

    const headers = { 'Authorization': `Bearer ${token}` };
    // Helper: kick off a fetch, parse JSON, run the setter when it
    // resolves, swallow errors so one bad endpoint doesn't take the rest
    // down with it. Returns the underlying promise for callers that want
    // to coordinate (e.g. the 403 access-denied check on the brief call).
    const kick = (url, onData) => fetch(url, { headers })
      .then((r) => {
        if (r.status === 403) {
          setAccessDenied(true);
          return null;
        }
        return r.json();
      })
      .then((data) => {
        if (data) onData(data);
        return data;
      })
      .catch((e) => {
        console.error(`[CEO_DASHBOARD] ${url} failed:`, e);
        return null;
      });

    // Fire every endpoint. Each one flips off the spinner via the
    // shared `firstFulfilled` flag the moment it returns — whichever
    // arrives first wins the race so the page paints as soon as
    // possible. We deliberately don't await Promise.all here so a slow
    // brief (the typical culprit) never blocks first paint.
    let firstFulfilled = false;
    const markRendered = () => {
      if (firstFulfilled) return;
      firstFulfilled = true;
      setLoading(false);
    };

    // Hard cap on the initial spinner so even a complete network stall
    // still hands the user a (mostly empty) dashboard within 3s instead
    // of trapping them on a spinner forever.
    const safetyTimer = setTimeout(markRendered, 3000);

    const tasks = [
      kick(`${API_URL}/api/ceo/brief/today`, (d) => { if (d.success) setBrief(d.data); }),
      kick(`${API_URL}/api/ceo/sales/snapshot?department=sales`, (d) => { if (d.success) setSalesSnapshot(d.data); }),
      kick(`${API_URL}/api/ceo/marketing/snapshot`, (d) => { if (d.success) setMarketingSnapshot(d.data); }),
      kick(`${API_URL}/api/ceo/sales/team?department=sales`, (d) => { if (d.success) setSalesTeam(d.data?.team || []); }),
      kick(`${API_URL}/api/ceo/sales/team?department=marketing`, (d) => { if (d.success) setMarketingTeam(d.data?.team || []); }),
      kick(`${API_URL}/api/ceo/competition/leaderboard`, (d) => { if (d.success) setCompetition(d.data); }),
      // Comments and voice messages share a timeline — merge them once
      // BOTH arrive (separate setters would race and overwrite each
      // other). Buffer them via local refs and re-merge on every update.
    ];

    // Comments + voice need to be merged together. Track both buffers
    // and re-merge whenever either updates.
    let textBuf = [];
    let voiceBuf = [];
    const remergeComments = () => {
      const merged = [...textBuf, ...voiceBuf].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      setComments(merged);
    };
    tasks.push(
      kick(`${API_URL}/api/ceo/comments`, (d) => {
        if (d.success) {
          textBuf = (d.data?.comments || []).map((c) => ({ ...c, _kind: 'text' }));
          remergeComments();
        }
      })
    );
    tasks.push(
      fetch(`${API_URL}/api/voice-messages/user/list?token=${encodeURIComponent(token)}&chat_type=hod_comments&limit=50`)
        .then((r) => r.json())
        .then((d) => {
          if (d?.success) {
            voiceBuf = (d.messages || []).map((v) => ({ ...v, _kind: 'voice' }));
            remergeComments();
          }
        })
        .catch(() => { /* non-fatal */ })
    );

    // Flip the spinner off the moment the FIRST endpoint resolves.
    // Promise.race resolves as soon as any of them does — including
    // failures, since we caught them inside `kick`.
    Promise.race(tasks).then(markRendered);
    // Final cleanup: when EVERY endpoint finishes (success or fail),
    // clear the safety timer so it can't fire stale.
    Promise.allSettled(tasks).finally(() => clearTimeout(safetyTimer));
  }, [token]);
  
  // Generate brief
  const handleGenerateBrief = async () => {
    setRefreshingBrief(true);
    try {
      const response = await fetch(`${API_URL}/api/ceo/brief/generate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        setBrief(result);
        toast.success('Brief generated successfully');
      } else {
        toast.error(result.error || 'Failed to generate brief');
      }
    } catch (error) {
      toast.error('Failed to generate brief');
    }
    setRefreshingBrief(false);
  };
  
  // Send comment
  const handleSendComment = async (commentData) => {
    try {
      const response = await fetch(`${API_URL}/api/ceo/comments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(commentData)
      });
      const result = await response.json();
      if (result.success) {
        setComments(prev => [{ ...result.data, _kind: 'text' }, ...prev]);
        toast.success('Comment sent to HOD');
      } else {
        toast.error(result.error || 'Failed to send comment');
      }
    } catch (error) {
      toast.error('Failed to send comment');
    }
  };
  
  // Prepend a freshly-uploaded voice note into the merged comments timeline.
  const handleVoiceSent = useCallback((voiceMsg) => {
    if (!voiceMsg) return;
    setComments(prev => [{ ...voiceMsg, _kind: 'voice' }, ...prev]);
  }, []);

  // Mark reply as read
  const handleMarkRead = async (commentId) => {
    try {
      await fetch(`${API_URL}/api/ceo/comments/${commentId}/read-reply`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setComments(prev => prev.map(c => 
        c.id === commentId ? { ...c, is_ceo_read_reply: true } : c
      ));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };
  
  // View agent report
  const handleViewReport = async (agent) => {
    setSelectedAgent(agent);
    setReportModalOpen(true);
    
    try {
      const response = await fetch(`${API_URL}/api/ceo/sales/agent/${agent.agent_id}/report`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        setAgentReport(result.data);
      }
    } catch (error) {
      console.error('Failed to fetch agent report:', error);
    }
  };
  
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 300000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, [fetchData]);
  
  // Access denied screen
  if (accessDenied) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-[#0a0a0f]' : 'bg-slate-50'}`}>
        <Card className={`max-w-md ${darkMode ? 'bg-[#1a1a2e] border-slate-700' : ''}`}>
          <CardContent className="p-8 text-center">
            <XCircle className="mx-auto mb-4 text-red-500" size={48} />
            <h2 className={`text-xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              Access Denied
            </h2>
            <p className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
              This dashboard is restricted to CEO access only.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-[#0a0a0f]' : 'bg-slate-50'}`}>
        <RefreshCw className="animate-spin text-violet-500" size={48} />
      </div>
    );
  }
  
  return (
    <div className={`min-h-screen ${darkMode ? 'bg-[#0a0a0f] text-white' : 'bg-slate-50'}`} data-testid="ceo-dashboard">
      <div className="max-w-[1800px] mx-auto p-6 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              <Building2 className="text-violet-500" size={28} />
              CEO Dashboard
            </h1>
            <p className={darkMode ? 'text-slate-400' : 'text-slate-500'}>
              Executive overview • {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          {/* Page-header action cluster — ordered left-to-right:
              1. Notification bell (with unread badge)
              2. Refresh
              3. + Assign Task (violet)
              4. All Dashboard dropdown (violet, matches Assign Task) */}
          <div className="flex items-center gap-2">
            {/* 1) Notification bell — moved here from the dark scope
                bar so it sits with the rest of the page actions. The
                wrapper `dark:text-white` keeps the bell readable in
                dark mode without inheriting the bar's white text. */}
            <div className={darkMode ? 'text-white' : 'text-slate-700'} data-testid="ceo-header-notifications">
              <NotificationBell />
            </div>

            {/* 2) Refresh */}
            <Button onClick={fetchData} variant="outline">
              <RefreshCw size={16} className="mr-2" />
              Refresh
            </Button>

            {/* 2.5) Chat — opens the Comments-to-HOD composer + thread
                list inside a floating dialog so the CEO can start or
                continue a conversation from any tab. */}
            <Button
              onClick={() => setIsChatDialogOpen(true)}
              variant="outline"
              data-testid="ceo-chat-btn"
            >
              <MessageSquare size={16} className="mr-2" />
              Chat
            </Button>

            {/* 2.75) AI Insights — opens the AI Executive Brief as a
                floating dialog so the CEO can review the daily brief
                without scrolling past it every time the dashboard
                loads. */}
            <Button
              onClick={() => setIsAIInsightsOpen(true)}
              className="bg-violet-600 hover:bg-violet-700 text-white shadow-sm"
              data-testid="ceo-ai-insights-btn"
            >
              <Sparkles size={16} className="mr-2" />
              AI Insights
            </Button>

            {/* 3) Assign Task — CEO can drop a task / reminder onto
                any agent in the org. Reuses /api/hod/tasks since that
                endpoint accepts CEO + HOD roles. */}
            <Button
              onClick={() => setIsTaskDialogOpen(true)}
              className="bg-violet-600 hover:bg-violet-700 text-white shadow-sm"
              data-testid="ceo-assign-task-btn"
            >
              <Plus size={16} className="mr-2" />
              Assign Task
            </Button>

            {/* 4) Population / role-view dropdown — styled as a violet
                pill to visually match the Assign Task button to its
                left. Drives the page content below. */}
            <div className="relative" data-testid="ceo-scope-population-header">
              <select
                value={dashboardScope}
                onChange={(e) => setDashboardScope(e.target.value)}
                className="appearance-none rounded-md bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold pl-4 pr-9 py-2 border border-violet-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-violet-300 shadow-sm transition"
                style={{ minWidth: '210px' }}
              >
                <option value="all" className="bg-white text-slate-800">All Dashboard</option>
                <option value="hod_sales" className="bg-white text-slate-800">HOD Sales Dashboard</option>
                <option value="hod_marketing" className="bg-white text-slate-800">HOD Marketing Dashboard</option>
                <option value="lead_dashboard" className="bg-white text-slate-800">Lead Dashboard</option>
                <option value="agents_overview" className="bg-white text-slate-800">Agents Dashboard Overview</option>
              </select>
              {/* Chevron in white to match the pill background */}
              <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-white">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2.5"
                     strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </div>

            {/* 5) Agents picker — only rendered when the role-view
                pill above is set to "Agents Dashboard Overview".
                Sits directly beside it so a CEO who picks that view
                immediately sees the agent selector appear next to it
                without scanning down to the scope bar. White pill so
                it visually separates from the violet view pill. */}
            {dashboardScope === 'agents_overview' && (
              <div className="relative" data-testid="ceo-agents-picker-header">
                <select
                  value={sliceBy}
                  onChange={(e) => setSliceBy(e.target.value)}
                  data-testid="scope-slice-select"
                  className="appearance-none rounded-md bg-white text-slate-800 text-sm font-medium pl-4 pr-9 py-2 border border-slate-300 cursor-pointer hover:border-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-300 shadow-sm transition"
                  style={{ minWidth: '180px' }}
                >
                  <option value="agents">Agents</option>
                  {agentRoster.length > 0 && (
                    <optgroup label="── Individual Agents ──">
                      {agentRoster.map((a) => (
                        <option key={a.id} value={`agent:${a.id}`}>
                          {a.name}
                          {a.role ? ` · ${String(a.role).replace(/_/g, ' ')}` : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="2.5"
                       strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </div>
            )}

            {/* 6) With Team checkbox — only shows when a specific
                agent has been picked from the Agents pill (so folding
                the team alongside that agent's data is meaningful).
                Hidden in all other states to keep the header clean. */}
            {dashboardScope === 'agents_overview'
              && String(sliceBy || '').startsWith('agent:') && (
              <label className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm select-none border cursor-pointer transition ${
                darkMode
                  ? 'text-white border-slate-600 hover:border-slate-400'
                  : 'text-slate-700 border-slate-300 hover:border-slate-400 bg-white'
              }`}
                     data-testid="ceo-with-team-header">
                <input
                  type="checkbox"
                  checked={withTeam}
                  onChange={(e) => setWithTeam(e.target.checked)}
                  className="w-4 h-4 rounded border-slate-400 text-violet-600 focus:ring-violet-300"
                />
                With Team
              </label>
            )}
          </div>
        </div>

        {/* The dark scope bar was removed entirely. All of its
            controls have either been moved into the page header
            (Notification bell, All Dashboard pill, Agents picker) or
            integrated inline where they belong (the "With Team"
            checkbox now sits next to the Agents picker in the
            header — see below). "Old Dashboard" was retired as a
            standalone control; the regular sidebar nav still has a
            link to /dashboard. */}

        {/* Active-view banner — visual confirmation that the dropdowns
            are driving the page below. Shows which population view is
            active and, if an agent is selected, who. Hidden when the
            user sits on the default "All Dashboard" + "Agents" combo
            so it doesn't add noise to the unfiltered state. */}
        {(() => {
          const showBanner =
            dashboardScope !== 'all' || selectedAgentInfo != null;
          if (!showBanner) return null;
          const scopeLabel = ({
            all:              'All Dashboard',
            hod_sales:        'HOD Sales Dashboard',
            hod_marketing:    'HOD Marketing Dashboard',
            lead_dashboard:   'Lead Dashboard',
            agents_overview:  'Agents Dashboard Overview',
          })[dashboardScope] || 'Custom view';
          return (
            <div
              className="flex items-center gap-3 mb-4 px-4 py-2.5 rounded-lg border border-violet-200 bg-violet-50 dark:bg-violet-900/20 dark:border-violet-700"
              data-testid="ceo-active-view-banner"
            >
              <div className="w-8 h-8 rounded-lg bg-violet-100 dark:bg-violet-800/60 flex items-center justify-center text-violet-700 dark:text-violet-200">
                <LayoutDashboard size={14} />
              </div>
              <div className="text-sm">
                <span className="text-slate-500 dark:text-slate-400">Now viewing</span>{' '}
                <span className="font-semibold text-slate-900 dark:text-white">{scopeLabel}</span>
                {selectedAgentInfo && (
                  <>
                    <span className="text-slate-400 mx-2">·</span>
                    <span className="text-slate-500 dark:text-slate-400">Agent</span>{' '}
                    <span className="font-semibold text-slate-900 dark:text-white">{selectedAgentInfo.name}</span>
                    {selectedAgentInfo.role && (
                      <span className="ml-2 text-[11px] text-slate-500 dark:text-slate-400">
                        ({String(selectedAgentInfo.role).replace(/_/g, ' ')})
                      </span>
                    )}
                    {withTeam && (
                      <span className="ml-2 text-[11px] px-2 py-0.5 rounded-full bg-violet-200 text-violet-800 font-medium">
                        + Team
                      </span>
                    )}
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={() => { setDashboardScope('all'); setSliceBy('agents'); setWithTeam(false); }}
                className="ml-auto text-xs text-violet-700 dark:text-violet-300 hover:underline"
              >
                Reset view
              </button>
            </div>
          );
        })()}

        {/* Revenue Performance widget intentionally not rendered on
            this page — it lives on /dashboard. */}

        {/* Leadership Overview block intentionally not rendered
            here — it lives on /dashboard. */}

        {/* ---- Agents Dashboard Overview mode ---------------------
            When the role-view dropdown is set to "Agents Dashboard
            Overview", the page swaps to the AgentsOverviewPanel —
            the per-agent KPI table (or single-agent drill-in when
            the second pill picks one). The rest of the operational
            CEO content (HOD embed, AI brief, department snapshots,
            field-tracking, etc.) is hidden so the page stays focused
            on agent data. */}
        {dashboardScope === 'agents_overview' && (
          <AgentsOverviewPanel
            agentId={selectedAgentInfo?.id || null}
            withTeam={withTeam}
          />
        )}

        {/* The HOD embed + the rest of the existing CEO operational
            content only renders for the All / HOD Sales / HOD
            Marketing modes. */}
        {/* Lead Dashboard scope — swap the HOD Dashboard scaffold for
            a CEO-focused Lead Dashboard summary (4 lead flows + top
            BDMs + aggregate funnel). Selecting this scope should NOT
            drop the CEO into the HOD view. */}
        {dashboardScope === 'lead_dashboard' && (
          <CEOLeadDashboardPanel token={token} darkMode={darkMode} />
        )}

        {dashboardScope !== 'agents_overview' && dashboardScope !== 'lead_dashboard' && (
        <>
        {/* HOD Dashboard View with Main Department Filter */}
        <HODDashboardView
          token={token}
          darkMode={darkMode}
          selectedDepartment={selectedDepartment}
          onDepartmentChange={handleDepartmentChange}
          agentRoster={agentRoster}
          comments={comments}
          onSendComment={handleSendComment}
          onMarkRead={handleMarkRead}
          onVoiceSent={handleVoiceSent}
        />
        
        {/* AI Daily Brief moved into the AI Insights dialog opened
            from the header button — see near the bottom of this
            component. Frees the main content area for the department
            snapshots and briefing panels. */}


        {/* Department Snapshots - Conditional Display */}
        {selectedDepartment === 'all' && (
          <div className="space-y-8 mt-8">
            {/* Field-tracking widgets are sales-only. CEO + leadership
                with mixed teams still see them. But anyone whose own
                role is a marketing role (a marketing HOD landing here
                via deep-link or impersonation) shouldn't be shown the
                agent geofence stack — it's noise for them. */}
            {(() => {
              const role = (user?.role || '').toLowerCase();
              const isMarketingOnly = role.includes('marketing');
              if (isMarketingOnly) return null;
              return (
                <>
                  {/* Phase 1: Agent Status + Response Time Row */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <AgentStatusDashboard compact={true} />
                    <ResponseTimeCard compact={true} />
                  </div>

                  {/* Phase 2: Live Location Map — CEO sees agents + HODs */}
                  <LiveLocationMap agentTypeFilter="all" roleScope="agents_and_hods" />

                  {/* Auto GPS Detection — geofenced site visits across the team */}
                  <TeamSiteVisitsPanel />

                  {/* Submitted site-visit reports across the team */}
                  <SiteVisitReportsPanel scope="team" />
                </>
              );
            })()}

            {/* Both Snapshots Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SalesSnapshotPanel data={salesSnapshot} token={token} onTargetsSaved={fetchData} />
              <MarketingSnapshotPanel data={marketingSnapshot} token={token} onTargetsSaved={fetchData} />
            </div>
            
            {/* Competition — Comments panel moved to the floating
                Chat button in the page header. */}
            <div>
              <CompetitionPreview data={competition} />
            </div>
            
            {/* Both Team Overviews Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SalesTeamOverview 
                team={salesTeam} 
                onViewReport={handleViewReport} 
              />
              <MarketingTeamOverview 
                team={marketingTeam} 
                onViewReport={handleViewReport} 
              />
            </div>
          </div>
        )}
        
        {selectedDepartment === 'sales' && (
          <div className="space-y-8 mt-8">
            {/* Operational header — Agent Status + Response Time +
                Team SLA. All scoped to SALES via `departmentFilter`
                so the CEO sees only sales agents' status, response
                times and SLA — matching what the Sales HOD sees on
                /hod. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <AgentStatusDashboard compact={true} departmentFilter="sales" />
              <ResponseTimeCard       compact={true} departmentFilter="sales" />
            </div>

            {/* Team SLA Monitor — org-wide for now (the component
                doesn't yet accept a department prop; tracked as a
                follow-up if per-dept SLA splits are needed). */}
            <TeamSLAMonitor />

            {/* Live Location Map — sales agents only via
                `agentTypeFilter="sales"`. Marketing roles are
                excluded so the map doesn't surface non-field staff. */}
            <LiveLocationMap agentTypeFilter="sales" roleScope="agents_and_hods" />
            <TeamSiteVisitsPanel />
            <SiteVisitReportsPanel scope="team" />

            {/* Sales Snapshot + Competition */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <SalesSnapshotPanel data={salesSnapshot} token={token} onTargetsSaved={fetchData} />
              </div>
              <div>
                <CompetitionPreview data={competition} />
              </div>
            </div>

            {/* Sales Team — Comments panel removed, use the Chat
                button in the page header instead. */}
            <div>
              <SalesTeamOverview
                team={salesTeam}
                onViewReport={handleViewReport}
              />
            </div>
          </div>
        )}
        
        {selectedDepartment === 'marketing' && (
          <div className="space-y-8 mt-8">
            {/* Operational header — Agent Status + Response Time +
                Team SLA, all scoped to MARKETING via
                `departmentFilter`. Field-visit / live-location
                panels are intentionally absent here because
                marketing teams don't run site visits. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <AgentStatusDashboard compact={true} departmentFilter="marketing" />
              <ResponseTimeCard       compact={true} departmentFilter="marketing" />
            </div>

            {/* Team SLA Monitor — org-wide for now. */}
            <TeamSLAMonitor />

            {/* Marketing Snapshot + Competition */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <MarketingSnapshotPanel data={marketingSnapshot} token={token} onTargetsSaved={fetchData} />
              </div>{/* marketing-only view */}
              <div>
                <CompetitionPreview data={competition} />
              </div>
            </div>

            {/* Marketing Team — Comments panel removed, use the
                Chat button in the page header instead. */}
            <div>
              <MarketingTeamOverview
                team={marketingTeam}
                onViewReport={handleViewReport}
              />
            </div>
          </div>
        )}
        </>
        )}
      </div>

      {/* Assign Task dialog — opens from the header "+ Assign Task"
          button. Posts to /api/hod/tasks. */}
      <Dialog
        open={isTaskDialogOpen}
        onOpenChange={(open) => { if (!open) { setIsTaskDialogOpen(false); resetTaskForm(); } }}
      >
        <DialogContent className="max-w-lg p-0 overflow-hidden">
          {/* Header — icon + title + subtitle. Softer violet tint so
              the dialog reads as a delegated-work capsule rather than
              a plain form. */}
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-violet-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
                <Plus size={20} />
              </div>
              <div className="flex-1">
                <DialogTitle className="text-lg font-semibold text-slate-900">Assign Task</DialogTitle>
                <p className="text-xs text-slate-500 mt-0.5">
                  Delegate a task or reminder directly to a team member.
                </p>
              </div>
            </div>
          </DialogHeader>

          {/* Body */}
          <div className="px-6 py-5 space-y-5 max-h-[65vh] overflow-y-auto">
            {/* Agent picker */}
            <div className="space-y-1.5">
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
                <User size={12} /> Assign to
              </label>
              <Select
                value={taskForm.agent_id}
                onValueChange={(v) => setTaskForm((f) => ({ ...f, agent_id: v }))}
              >
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Pick an agent…" />
                </SelectTrigger>
                <SelectContent>
                  {agentRoster.length === 0 ? (
                    <SelectItem value="__none__" disabled>No agents available</SelectItem>
                  ) : agentRoster.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                      {a.role ? ` · ${String(a.role).replace(/_/g, ' ')}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Type + Priority side-by-side */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Type</label>
                <Select
                  value={taskForm.type}
                  onValueChange={(v) => setTaskForm((f) => ({ ...f, type: v }))}
                >
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="task">Task</SelectItem>
                    <SelectItem value="reminder">Reminder</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Priority</label>
                <Select
                  value={taskForm.priority}
                  onValueChange={(v) => setTaskForm((f) => ({ ...f, priority: v }))}
                >
                  <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Title */}
            <div className="space-y-1.5">
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Title</label>
              <Input
                value={taskForm.title}
                onChange={(e) => setTaskForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Follow up with Bayz 102 buyers"
                className="h-10"
              />
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Description</label>
              <Textarea
                value={taskForm.description}
                onChange={(e) => setTaskForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Optional notes for the agent"
                rows={3}
                className="resize-none"
              />
            </div>

            {/* Due date + time grouped in a soft card so it reads as
                one "when" section, not two floating inputs. */}
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3">
                When
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs text-slate-600 font-medium">Due date</label>
                  <Input
                    type="date"
                    value={taskForm.due_date}
                    onChange={(e) => setTaskForm((f) => ({ ...f, due_date: e.target.value }))}
                    className="h-10 bg-white"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs text-slate-600 font-medium">Due time</label>
                  <Input
                    type="time"
                    value={taskForm.due_time}
                    onChange={(e) => setTaskForm((f) => ({ ...f, due_time: e.target.value }))}
                    className="h-10 bg-white"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <DialogFooter className="px-6 py-4 border-t bg-slate-50/70 gap-2">
            <Button
              variant="outline"
              onClick={() => { setIsTaskDialogOpen(false); resetTaskForm(); }}
              disabled={creatingTask}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTask}
              disabled={creatingTask || !taskForm.agent_id || !taskForm.title.trim()}
              className="bg-violet-600 hover:bg-violet-700 text-white shadow-sm"
            >
              {creatingTask ? (
                <><RefreshCw size={14} className="mr-2 animate-spin" /> Assigning…</>
              ) : (
                <><Plus size={14} className="mr-2" /> Assign Task</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Chat dialog — floating Comments-to-HOD composer + thread list,
          reusing CEOCommentsSection so behaviour matches the CEO
          Comments tab exactly (same send / mark-read / voice hooks). */}
      <Dialog
        open={isChatDialogOpen}
        onOpenChange={(open) => setIsChatDialogOpen(!!open)}
      >
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2 border-b">
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare size={18} className="text-violet-500" />
              CEO Chat
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto p-6 flex-1">
            <CEOCommentsSection
              comments={comments}
              onSendComment={handleSendComment}
              onMarkRead={handleMarkRead}
              onVoiceSent={handleVoiceSent}
              token={token}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Insights dialog — surfaces the AI Executive Brief
          (Today's Briefing + full markdown, date picker, share,
          download, refresh) as a floating popup. Same component,
          same actions — just off the main scroll path so the CEO
          Dashboard opens on tabs and snapshots, not a wall of AI
          narrative. */}
      <Dialog
        open={isAIInsightsOpen}
        onOpenChange={(open) => setIsAIInsightsOpen(!!open)}
      >
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b bg-gradient-to-r from-violet-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
                <Sparkles size={20} />
              </div>
              <div className="flex-1">
                <DialogTitle className="text-lg font-semibold text-slate-900">
                  AI Insights
                </DialogTitle>
                <p className="text-xs text-slate-500 mt-0.5">
                  Today's Executive Brief — auto-generated from live operational data.
                </p>
              </div>
            </div>
          </DialogHeader>
          <div className="overflow-y-auto p-6 flex-1">
            <AIDailyBrief
              brief={brief}
              onRefresh={handleGenerateBrief}
              refreshing={refreshingBrief}
              token={token}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Agent Report Modal */}
      <AgentReportModal
        agent={selectedAgent}
        report={agentReport}
        isOpen={reportModalOpen}
        token={token}
        onClose={() => {
          setReportModalOpen(false);
          setAgentReport(null);
        }}
      />
    </div>
  );
};

export default CEODashboard;
